import React, { useEffect, useMemo, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useScreenInit } from '../useScreenInit.js';
import { SignupShowcase } from '../components/SignupShowcase';
import { DarkGreenTexture } from '../components/DarkGreenTexture';
import { WaitlistModal } from '../components/WaitlistModal';
import { Check, ChevronDown, Shield, Lock, FileText, ArrowRight, ArrowLeft, Info, X, Search, Hash, MessageSquare, Headphones, Users, Grid3x3, Bell, Star, CreditCard, Calendar, ShieldCheck, Loader2, ArrowLeftRight, Plug, UserPlus, Sparkles, CheckCircle, ChevronRight, LayoutGrid } from 'lucide-react';
const ALL_TOOLS = [{
  id: 'slack',
  name: 'Slack',
  icon: 'slack',
  category: 'communication'
}, {
  id: 'gmail',
  name: 'Gmail',
  icon: 'gmail',
  category: 'communication'
}, {
  id: 'outlook',
  name: 'Outlook',
  icon: 'microsoftoutlook',
  category: 'communication'
}, {
  id: 'teams',
  name: 'Microsoft Teams',
  icon: 'microsoftteams',
  category: 'communication'
}, {
  id: 'discord',
  name: 'Discord',
  icon: 'discord',
  category: 'communication'
}, {
  id: 'googledrive',
  name: 'Google Drive',
  icon: 'googledrive',
  category: 'docs'
}, {
  id: 'notion',
  name: 'Notion',
  icon: 'notion',
  category: 'docs'
}, {
  id: 'confluence',
  name: 'Confluence',
  icon: 'confluence',
  category: 'docs'
}, {
  id: 'coda',
  name: 'Coda',
  icon: 'coda',
  category: 'docs'
}, {
  id: 'dropbox',
  name: 'Dropbox',
  icon: 'dropbox',
  category: 'docs'
}, {
  id: 'github',
  name: 'GitHub',
  icon: 'github',
  category: 'engineering'
}, {
  id: 'gitlab',
  name: 'GitLab',
  icon: 'gitlab',
  category: 'engineering'
}, {
  id: 'bitbucket',
  name: 'Bitbucket',
  icon: 'bitbucket',
  category: 'engineering'
}, {
  id: 'sentry',
  name: 'Sentry',
  icon: 'sentry',
  category: 'engineering'
}, {
  id: 'datadog',
  name: 'Datadog',
  icon: 'datadog',
  category: 'engineering'
}, {
  id: 'linear',
  name: 'Linear',
  icon: 'linear',
  category: 'project'
}, {
  id: 'jira',
  name: 'Jira',
  icon: 'jira',
  category: 'project'
}, {
  id: 'asana',
  name: 'Asana',
  icon: 'asana',
  category: 'project'
}, {
  id: 'trello',
  name: 'Trello',
  icon: 'trello',
  category: 'project'
}, {
  id: 'monday',
  name: 'Monday.com',
  icon: 'mondaydotcom',
  category: 'project'
}, {
  id: 'salesforce',
  name: 'Salesforce',
  icon: 'salesforce',
  category: 'crm'
}, {
  id: 'hubspot',
  name: 'HubSpot',
  icon: 'hubspot',
  category: 'crm'
}, {
  id: 'pipedrive',
  name: 'Pipedrive',
  icon: 'pipedrive',
  category: 'crm'
}, {
  id: 'zendesk',
  name: 'Zendesk',
  icon: 'zendesk',
  category: 'support'
}, {
  id: 'intercom',
  name: 'Intercom',
  icon: 'intercom',
  category: 'support'
}, {
  id: 'front',
  name: 'Front',
  icon: 'front',
  category: 'support'
}, {
  id: 'stripe',
  name: 'Stripe',
  icon: 'stripe',
  category: 'finance'
}, {
  id: 'quickbooks',
  name: 'QuickBooks',
  icon: 'quickbooks',
  category: 'finance'
}, {
  id: 'figma',
  name: 'Figma',
  icon: 'figma',
  category: 'design'
}, {
  id: 'miro',
  name: 'Miro',
  icon: 'miro',
  category: 'design'
}, {
  id: 'googleanalytics',
  name: 'Google Analytics',
  icon: 'googleanalytics',
  category: 'analytics'
}, {
  id: 'mixpanel',
  name: 'Mixpanel',
  icon: 'mixpanel',
  category: 'analytics'
}, {
  id: 'amplitude',
  name: 'Amplitude',
  icon: 'amplitude',
  category: 'analytics'
}, {
  id: 'mailchimp',
  name: 'Mailchimp',
  icon: 'mailchimp',
  category: 'marketing'
}, {
  id: 'googleads',
  name: 'Google Ads',
  icon: 'googleads',
  category: 'marketing'
}, {
  id: 'hootsuite',
  name: 'Hootsuite',
  icon: 'hootsuite',
  category: 'marketing'
}];
function getScopesForTool(tool: any) {
  if (tool.id === 'slack') {
    return [{
      id: 'read_channels',
      label: 'Read channel messages',
      description: "View messages in public channels you're a member of.",
      required: true
    }, {
      id: 'search_history',
      label: 'Search history',
      description: "Search across messages so Giga can cite what's been said.",
      required: true
    }, {
      id: 'read_files',
      label: 'Read files & attachments',
      description: 'Read files shared in channels and DMs.',
      required: false
    }, {
      id: 'read_dms',
      label: 'Read your direct messages',
      description: 'Index your DMs so Giga can find specific conversations.',
      required: false
    }, {
      id: 'post',
      label: 'Post on your behalf',
      description: 'Reply in channels and send DMs when you ask Giga to.',
      required: false
    }];
  }
  if (tool.category === 'docs') {
    return [{
      id: 'read_files',
      label: 'Read your files & folders',
      description: `Index docs, sheets, slides and PDFs in your ${tool.name}.`,
      required: true
    }, {
      id: 'read_shared',
      label: 'Read shared drives',
      description: 'Include drives shared with you by your team.',
      required: true
    }, {
      id: 'write_docs',
      label: 'Create & edit documents',
      description: 'Draft new docs when you ask Giga to.',
      required: false
    }];
  }
  if (tool.category === 'communication') {
    return [{
      id: 'read_msgs',
      label: 'Read messages & channels',
      description: 'Index conversations you have access to.',
      required: true
    }, {
      id: 'send_msgs',
      label: 'Send messages',
      description: 'Post updates and replies on your behalf.',
      required: true
    }];
  }
  if (tool.category === 'engineering' || tool.category === 'project') {
    return [{
      id: 'read_issues',
      label: 'Read issues & projects',
      description: 'Index tickets, PRs, and project states.',
      required: true
    }, {
      id: 'write_issues',
      label: 'Update issues',
      description: 'Change statuses and add comments.',
      required: false
    }];
  }
  return [{
    id: 'read_data',
    label: `Read ${tool.name} data`,
    description: 'Index records and objects you have access to.',
    required: true
  }, {
    id: 'write_data',
    label: `Update ${tool.name} records`,
    description: 'Create and modify records on your behalf.',
    required: false
  }];
}
function SlackIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6347">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-6348" />
      <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#36C5F0" data-id="element-6349" />
      <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-6350" />
      <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-6351" />
    </svg>;
}
const BUSINESS_TYPES = ['E-commerce / DTC', 'Consumer app', 'SaaS / B2B software', 'Marketplace', 'Fintech', 'Healthcare / health-tech', 'Education / ed-tech', 'Media & entertainment', 'Gaming', 'AI / ML', 'Hardware / IoT', 'Real estate / proptech', 'Travel & hospitality', 'Food & beverage', 'Retail (brick & mortar)', 'Logistics & supply chain', 'Professional services', 'Agency / consulting', 'Nonprofit', 'Government / public sector', 'Manufacturing', 'Energy & climate', 'Crypto / web3', 'Other'];
// The connected Slack workspace. In production this comes from the Slack OAuth
// handshake (team name) so the post-connect screens address the user by their
// real workspace instead of a placeholder. Mirrors the account the user
// "connects" earlier in the flow (trackrheadquarters.slack.com).
const WORKSPACE_NAME = 'Trackr HQ';
const WORKSPACE_INITIAL = WORKSPACE_NAME.charAt(0);
function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search…',
  ariaLabel,
  searchable = true
}: {
  value: string;
  onChange: (val: string) => void;
  options: string[];
  placeholder?: string;
  searchPlaceholder?: string;
  ariaLabel?: string;
  searchable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.toLowerCase().includes(q));
  }, [query, options]);
  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  // Focus the search field when the panel opens (only when searchable)
  useEffect(() => {
    if (open && searchable) {
      setQuery('');
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open, searchable]);
  return <div ref={wrapperRef} className="relative" data-id="element-6352">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border bg-white text-left focus:outline-none transition-colors ${open ? 'border-[#EA580C]' : 'border-neutral-200'} ${value ? 'text-[#333333]' : 'text-neutral-400'}`} data-id="element-6353">
        <span className="truncate" data-id="element-6354">{value || placeholder}</span>
        <ChevronDown size={18} className={`shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-6355" />
      </button>

      <AnimatePresence data-id="element-6356">
        {open && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute z-30 mt-2 w-full rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden" data-id="element-6357">
            {searchable && <div className="p-2 border-b border-neutral-100" data-id="element-6358">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200 focus-within:border-[#EA580C] transition-colors" data-id="element-6359">
                  <Search size={16} className="text-neutral-400 shrink-0" data-id="element-6360" />
                  <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder={searchPlaceholder} className="w-full bg-transparent text-sm text-[#333333] placeholder:text-neutral-400 focus:outline-none" data-id="element-6361" />
                </div>
              </div>}
            <ul role="listbox" className="max-h-60 overflow-y-auto py-1" data-id="element-6362">
              {filtered.length === 0 ? <li className="px-4 py-3 text-sm text-neutral-400" data-id="element-6363">
                  No matches found
                </li> : filtered.map(option => {
            const isSelected = option === value;
            return <li key={option} role="option" aria-selected={isSelected} data-id="element-6364">
                      <button type="button" onClick={() => {
                onChange(option);
                setOpen(false);
              }} className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left text-sm transition-colors ${isSelected ? 'bg-[#FFEDD5] text-[#333333] font-medium' : 'text-[#333333] hover:bg-neutral-50'}`} data-id="element-6365">
                        <span className="truncate" data-id="element-6366">{option}</span>
                        {isSelected && <Check size={16} className="text-[#EA580C] shrink-0" strokeWidth={2.5} data-id="element-6367" />}
                      </button>
                    </li>;
          })}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function MultiSelectDropdown({
  values,
  onChange,
  options,
  placeholder = 'Select options'
}: {
  values: string[];
  onChange: (val: string) => void;
  options: string[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  return <div ref={wrapperRef} className="relative" data-id="element-6368">
      <button type="button" onClick={() => setOpen(o => !o)} className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border bg-white text-left focus:outline-none transition-colors ${open ? 'border-[#EA580C]' : 'border-neutral-200'} ${values.length > 0 ? 'text-[#333333]' : 'text-neutral-400'}`} data-id="element-6369">
        <span className="truncate" data-id="element-6370">
          {values.length === 0 ? placeholder : values.length === 1 ? values[0] : `${values.length} selected`}
        </span>
        <ChevronDown size={18} className={`shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-6371" />
      </button>

      <AnimatePresence data-id="element-6372">
        {open && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute z-30 mt-2 w-full rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden" data-id="element-6373">
            <ul role="listbox" className="max-h-60 overflow-y-auto py-1" data-id="element-6374">
              {options.map(option => {
            const isSelected = values.includes(option);
            return <li key={option} role="option" aria-selected={isSelected} data-id="element-6375">
                    <button type="button" onClick={() => onChange(option)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${isSelected ? 'bg-[#FFEDD5]' : 'hover:bg-neutral-50'}`} data-id="element-6376">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-[#EA580C] border-[#EA580C]' : 'border-neutral-300 bg-white'}`} data-id="element-6377">
                        {isSelected && <Check size={12} className="text-white" strokeWidth={3} data-id="element-6378" />}
                      </div>
                      <span className={`truncate ${isSelected ? 'text-[#333333] font-medium' : 'text-[#333333]'}`} data-id="element-6379">
                        {option}
                      </span>
                    </button>
                  </li>;
          })}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function VisibilityDropdown({
  value,
  onChange
}: {
  value: 'team' | 'private';
  onChange: (val: 'team' | 'private') => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  const isPrivate = value === 'private';
  const OPTIONS: {
    id: 'team' | 'private';
    Icon: typeof Users;
    label: string;
  }[] = [{
    id: 'team',
    Icon: Users,
    label: 'Team-only'
  }, {
    id: 'private',
    Icon: Lock,
    label: 'Private (invite only)'
  }];
  return <div ref={wrapperRef} className="relative" data-id="element-6380">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label="Who can use this connection?" className={`inline-flex items-center gap-1.5 pl-2 pr-1.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${open ? 'border-[#333333] bg-white' : 'border-neutral-200 bg-neutral-50 hover:border-neutral-300'} text-neutral-600`} data-id="element-6381">
        {isPrivate ? <Lock size={11} data-id="element-6382" /> : <Users size={11} data-id="element-6383" />}
        {isPrivate ? 'Private' : 'Team-only'}
        <ChevronDown size={12} className={`text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-6384" />
      </button>

      <AnimatePresence data-id="element-6385">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.14,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute right-0 z-30 mt-2 w-60 rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden" role="listbox" data-id="element-6386">
            <div className="px-3 pt-2.5 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-6387">
              Who should have access?
            </div>
            {OPTIONS.map(opt => {
          const active = opt.id === value;
          return <button key={opt.id} type="button" role="option" aria-selected={active} onClick={() => {
            onChange(opt.id);
            setOpen(false);
          }} className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors ${active ? 'text-[#333333] font-medium' : 'text-[#333333] hover:bg-neutral-50'}`} data-id="element-6388">
                  <opt.Icon size={15} className="text-neutral-500 shrink-0" data-id="element-6389" />
                  <span className="flex-1 truncate" data-id="element-6390">{opt.label}</span>
                  {active && <Check size={15} className="text-[#EA580C] shrink-0" strokeWidth={2.5} data-id="element-6391" />}
                </button>;
        })}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function StickyRevealBar({
  children
}: {
  children: React.ReactNode;
}) {
  // A persistent fixed action bar pinned to the bottom of the screen. It stays
  // visible no matter where the user is in the scroll, with a one-time slide-up
  // entrance on mount. Self-contained so it never affects the page hook order.
  return <motion.div initial={{
    y: 90,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    type: 'spring',
    stiffness: 380,
    damping: 34,
    delay: 0.1
  }} className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white/95 backdrop-blur-sm shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.12)]" data-id="element-6392">
      <div className="max-w-4xl mx-auto px-6 py-4" data-id="element-6393">{children}</div>
    </motion.div>;
}
// Maps the catalog's simpleicons-style slugs to the full-color
// dashboard-icons slugs so each tool shows its real brand logo.
const DASHBOARD_ICON_SLUGS: Record<string, string> = {
  microsoftoutlook: 'outlook',
  microsoftteams: 'microsoft-teams',
  googledrive: 'google-drive',
  mondaydotcom: 'monday',
  googleanalytics: 'google-analytics',
  googleads: 'google-ads'
};
function ToolLogo({
  name,
  icon,
  className = 'w-12 h-12 mx-auto mb-3',
  fallbackTextSize = 'text-lg'
}: {
  name: string;
  icon: string;
  className?: string;
  fallbackTextSize?: string;
}) {
  const [failed, setFailed] = useState(false);
  const slug = DASHBOARD_ICON_SLUGS[icon] ?? icon;
  if (failed) {
    return <div className={`${className} rounded-xl bg-[#FFEDD5] flex items-center justify-center`} data-id="element-6394">
        <span className={`${fallbackTextSize} font-title font-bold text-[#333333]`} data-id="element-6395">
          {name.charAt(0).toUpperCase()}
        </span>
      </div>;
  }
  return <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`} alt={name} onError={() => setFailed(true)} className={`${className} object-contain`} data-id="element-6396" />;
}
function TeamsIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6397">
      <path d="M16.5 13.5c1.93 0 3.5-1.57 3.5-3.5s-1.57-3.5-3.5-3.5-3.5 1.57-3.5 3.5 1.57 3.5 3.5 3.5z" fill="#5059C9" data-id="element-6398" />
      <path d="M22.5 19.5v-1.5c0-1.93-1.57-3.5-3.5-3.5h-5c-1.93 0-3.5 1.57-3.5 3.5v1.5h12z" fill="#5059C9" data-id="element-6399" />
      <path d="M7.5 12c1.93 0 3.5-1.57 3.5-3.5S9.43 5 7.5 5 4 6.57 4 8.5 5.57 12 7.5 12z" fill="#7B83EB" data-id="element-6400" />
      <path d="M13.5 19.5v-1.5c0-1.93-1.57-3.5-3.5-3.5h-5c-1.93 0-3.5 1.57-3.5 3.5v1.5h12z" fill="#7B83EB" data-id="element-6401" />
    </svg>;
}
function GigaHeader({
  hideOnMobile = false
}: {
  hideOnMobile?: boolean;
}) {
  return <div className={`border-b border-neutral-100 px-6 py-4 ${hideOnMobile ? 'hidden lg:block' : ''}`} data-id="element-6402">
      <Link to="/" aria-label="Back to Giga home" className="inline-flex items-center gap-2 group w-fit" data-id="element-6403">
        <div className="w-8 h-8 rounded-lg bg-[#EA580C] flex items-center justify-center group-hover:scale-105 transition-transform" data-id="element-6404">
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6405" />
        </div>
        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/giga4x.png" alt="Giga" className="h-5 object-contain" data-id="element-6406" />
      </Link>
    </div>;
}
export function Signup() {
  const screenInit = useScreenInit();
  const location = useLocation();
  const navigate = useNavigate();
  // The /login route reuses this component but should read as a returning-user
  // sign-in, not a trial pitch.
  const isLogin = location.pathname === '/login';
  // The feature isn't live yet — the public "Get started" CTAs send users here
  // with ?waitlist=1, which drops them on the Slack connect screen (step 2)
  // with the waitlist popup open over it.
  const waitlistParam = new URLSearchParams(location.search).get('waitlist') === '1';
  const [step, setStep] = useState(screenInit.step ?? (waitlistParam ? 2 : isLogin ? 10 : 1));
  const [showWaitlist, setShowWaitlist] = useState(waitlistParam);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  // Step 10: once the user signs out of all workspaces, the remembered
  // workspace list disappears and the screen falls back to a clean sign-in.
  const [signedOut, setSignedOut] = useState(false);
  // Step 10: Microsoft Teams isn't live yet, so the "Join Microsoft Teams
  // waitlist" button opens an inline email capture and confirms once submitted.
  const [teamsWaitlistOpen, setTeamsWaitlistOpen] = useState(false);
  const [teamsWaitlistEmail, setTeamsWaitlistEmail] = useState('');
  const [teamsWaitlistDone, setTeamsWaitlistDone] = useState(false);
  const submitTeamsWaitlist = (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamsWaitlistEmail.trim()) return;
    setTeamsWaitlistDone(true);
  };
  // Toggle a tool in/out of the selected set (used by the step 3 tool grid)
  const toggleTool = (id: string) => {
    setSelectedTools(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  };
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [toolSearch, setToolSearch] = useState('');
  // Step 5: "Tell us more about you" select answers
  const [foundUs, setFoundUs] = useState('');
  const [role, setRole] = useState('');
  // Step 5: what kind of business the user runs (required, searchable)
  const [businessType, setBusinessType] = useState('');
  // Step 5: which AI Teammate(s) the user wants to create first (required, multi-select)
  const [firstTeammates, setFirstTeammates] = useState<string[]>([]);
  const toggleFirstTeammate = (option: string) => setFirstTeammates(prev => prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]);
  // Step 6: Functional roles for tool recommendations (multi-select)
  const [funcRoles, setFuncRoles] = useState<string[]>([]);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  // Step 6: integrations the user requested when not found in the catalog
  const [requestedTools, setRequestedTools] = useState<string[]>([]);
  // Step 6: optional link the user can paste for a tool we don't list yet
  const [requestedToolLink, setRequestedToolLink] = useState('');
  // Step 11: selected trial plan
  const [plan, setPlan] = useState('plus');
  // Step 7: per-tool OAuth-style connection flow
  const [connectedConns, setConnectedConns] = useState<string[]>([]);
  // Whether the user has kicked off the connection sequence with the start button
  const [connectionsStarted, setConnectionsStarted] = useState(false);
  const [permModal, setPermModal] = useState<null | {
    id: string;
    name: string;
    icon: string;
    scopes: any[];
  }>(null);
  const [connectPhase, setConnectPhase] = useState<'consent' | 'handing-off' | null>(null);
  // Step 3: Slack OAuth consent — per-permission checkbox state. The two
  // REQUIRED scopes stay locked on; the rest are user-toggleable.
  const [slackOAuthScopes, setSlackOAuthScopes] = useState<Record<string, boolean>>({
    readChannels: true,
    searchHistory: true,
    readFiles: true,
    readDMs: false,
    postOnBehalf: true
  });
  // Step 7: local state for optional permission checkboxes in the modal
  const [optionalScopes, setOptionalScopes] = useState<Record<string, boolean>>({});
  // Step 7: per-tool integration visibility — 'private' (only you) or 'team'
  // (shared with the whole workspace).
  const [connVisibility, setConnVisibility] = useState<Record<string, 'private' | 'team'>>({});
  const USE_CASE_RECS: Record<string, string[]> = {
    'Marketing & Ad Campaigns': ['googleanalytics', 'googleads', 'mailchimp', 'hubspot', 'notion', 'slack'],
    'Operations & Workflow Automation': ['notion', 'slack', 'googledrive', 'asana', 'monday', 'quickbooks'],
    'Data analysis & reporting': ['googleanalytics', 'mixpanel', 'amplitude', 'notion', 'slack', 'github'],
    'Sales & CRM': ['salesforce', 'hubspot', 'pipedrive', 'slack', 'gmail', 'googledrive'],
    'Content & Creative Production': ['figma', 'miro', 'notion', 'slack', 'googledrive', 'dropbox'],
    'Engineering & Development': ['github', 'linear', 'slack', 'sentry', 'googledrive', 'notion'],
    'Executive / Leadership Assistant': ['slack', 'gmail', 'notion', 'googledrive', 'stripe', 'salesforce'],
    'Agency/Consultancy & Client Operations': ['slack', 'notion', 'googledrive', 'asana', 'trello', 'zoom'],
    'eCommerce & Shopify': ['shopify', 'stripe', 'mailchimp', 'googleanalytics', 'slack', 'zendesk'],
    Other: ['slack', 'notion', 'googledrive', 'gmail']
  };
  useEffect(() => {
    if (step === 6 && selectedTools.length === 0) {
      const activeRoles = funcRoles.length > 0 ? funcRoles : firstTeammates.length > 0 ? firstTeammates : ['Engineering & Development'];
      if (funcRoles.length === 0) {
        setFuncRoles(activeRoles);
      }
      const initialTools = new Set<string>();
      activeRoles.forEach(r => {
        USE_CASE_RECS[r]?.forEach(t => initialTools.add(t));
      });
      if (initialTools.size === 0) {
        USE_CASE_RECS['Engineering & Development'].forEach(t => initialTools.add(t));
      }
      setSelectedTools(Array.from(initialTools));
    }
  }, [step, firstTeammates, funcRoles.length, selectedTools.length]);
  // Step 7: connections to make — derived from the tools the user selected.
  // Falls back to a sensible default set when nothing was selected (e.g. when
  // the screen is opened directly via screen state).
  const step7Connections = useMemo(() => {
    const ids = selectedTools.length > 0 ? selectedTools : ['slack', 'googledrive', 'notion', 'github', 'linear', 'sentry'];
    return ids.map(id => {
      const tool = ALL_TOOLS.find(t => t.id === id) || {
        id,
        name: id,
        icon: id,
        category: 'other'
      };
      return {
        ...tool,
        scopes: getScopesForTool(tool)
      };
    });
  }, [selectedTools]);
  const step7ActiveIdx = step7Connections.findIndex(c => !connectedConns.includes(c.id));
  const step7AllDone = step7Connections.length > 0 && connectedConns.length === step7Connections.length;
  // Landing/left-pane animation state. These hooks MUST live above the
  // per-step early returns below — otherwise renders that return early would
  // run fewer hooks than renders that fall through, tripping React #300.
  const LANDING_TESTIMONIAL_COUNT = 3;
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % LANDING_TESTIMONIAL_COUNT);
    }, 7000);
    return () => clearInterval(testimonialInterval);
  }, []);
  // Setup checklist progress — the rows resolve one-by-one (pending → done) so
  // the card reads like a live, sub-60-second setup actually happening.
  const SETUP_STEPS = 3;
  const [setupStep, setSetupStep] = useState(0);
  useEffect(() => {
    const start = setTimeout(() => {
      const id = setInterval(() => {
        setSetupStep(prev => {
          if (prev >= SETUP_STEPS) {
            clearInterval(id);
            return prev;
          }
          return prev + 1;
        });
      }, 750);
      (window as any).__gigaSetupInterval = id;
    }, 600);
    return () => {
      clearTimeout(start);
      clearInterval((window as any).__gigaSetupInterval);
    };
  }, []);
  // Step 2: Slack Consent (Workspace Selection)
  if (step === 2) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15,
      ease: 'easeOut'
    }} className="min-h-screen bg-white sm:bg-[#FAF8F4] font-body flex flex-col sm:items-center sm:justify-center sm:p-6" data-id="element-6407">
        {/* Waitlist popup — opens immediately over the Slack connect screen
                                                    while the feature is invite-only. */}
        <AnimatePresence data-id="element-6408">
          {showWaitlist && <WaitlistModal referredBy={new URLSearchParams(location.search).get('ref')?.trim().toLowerCase()} onClose={() => {
          setShowWaitlist(false);
          navigate('/');
        }} data-id="element-6409" />}
        </AnimatePresence>

        <div className="w-full sm:max-w-lg bg-white sm:rounded-3xl sm:shadow-xl sm:border border-neutral-200 flex flex-col min-h-screen sm:min-h-0" data-id="element-6410">
          <div className="flex-1 p-6 sm:p-10 overflow-auto" data-id="element-6411">
            <h1 className="text-2xl font-title font-bold text-[#333333] text-center mb-6" data-id="element-6412">
              Sign in to Giga with Slack
            </h1>

            {/* Slack ⇄ Giga lockup */}
            <div className="flex items-center justify-center gap-4 mb-8" data-id="element-6413">
              <div className="w-14 h-14 rounded-2xl bg-[#2B211A] flex items-center justify-center shadow-sm" data-id="element-6414">
                <SlackIcon size={28} data-id="element-6415" />
              </div>
              <ArrowLeftRight size={22} className="text-neutral-400" data-id="element-6416" />
              <div className="w-14 h-14 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-sm" data-id="element-6417">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="Giga" className="w-7 h-7 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6418" />
              </div>
            </div>

            {/* Account / workspace selector */}
            <p className="text-sm font-bold text-[#2B211A] mb-2" data-id="element-6419">
              Select a Slack account or workspace
            </p>
            <button onClick={() => setStep(3)} className="w-full flex items-center justify-between gap-3 p-4 rounded-xl border border-neutral-200 hover:border-[#EA580C] hover:bg-[#FFEDD5]/30 transition-all group mb-6 text-left" data-id="element-6420">
              <div className="flex items-center gap-3 min-w-0" data-id="element-6421">
                <div className="w-11 h-11 rounded-lg bg-[#611f69] flex items-center justify-center text-white font-bold shrink-0" data-id="element-6422">
                  F
                </div>
                <div className="min-w-0" data-id="element-6423">
                  <div className="font-bold text-[#2B211A] text-[15px] truncate" data-id="element-6424">
                    Faith Alqahtani
                  </div>
                  <div className="text-xs text-neutral-500 truncate" data-id="element-6425">
                    alqahtani.faith@gmail.com · trackrheadquarters.slack.com
                  </div>
                </div>
              </div>
              <ChevronDown size={18} className="text-neutral-400 group-hover:text-[#EA580C] shrink-0" data-id="element-6426" />
            </button>

            {/* Account-details usage */}
            <div className="rounded-2xl bg-neutral-50 border border-neutral-100 p-5 mb-6" data-id="element-6427">
              <p className="text-sm font-bold text-[#2B211A] mb-4" data-id="element-6428">
                Your account details will be used to:
              </p>
              <ul className="space-y-4" data-id="element-6429">
                <li className="flex items-start gap-3 text-sm text-neutral-600" data-id="element-6430">
                  <Plug size={18} className="text-[#2B211A] shrink-0 mt-0.5" data-id="element-6431" />
                  <span data-id="element-6432">
                    Establish a verified connection between an existing Giga
                    account and Slack.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm text-neutral-600" data-id="element-6433">
                  <UserPlus size={18} className="text-[#2B211A] shrink-0 mt-0.5" data-id="element-6434" />
                  <span data-id="element-6435">
                    Help you create a new Giga account, if you don't already
                    have one.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm text-neutral-600" data-id="element-6436">
                  <Sparkles size={18} className="text-[#2B211A] shrink-0 mt-0.5" data-id="element-6437" />
                  <span data-id="element-6438">
                    Unlock Giga features in Slack, like configurable
                    notifications, rich link previews, and quick actions.
                  </span>
                </li>
              </ul>
            </div>

            {/* Privacy disclosure */}
            <p className="text-xs text-neutral-500 leading-relaxed mb-0 sm:mb-6" data-id="element-6439">
              Slack will share your name, email address, profile image, user ID,
              and team ID with Giga. View Slack's{' '}
              <a href="#" className="text-[#EA580C] hover:underline" data-id="element-6440">
                privacy policy
              </a>
              . By agreeing to allow Slack to share your identity, you are also
              agreeing to Giga's{' '}
              <a href="#" className="text-[#EA580C] hover:underline" data-id="element-6441">
                privacy agreement
              </a>{' '}
              and{' '}
              <a href="#" className="text-[#EA580C] hover:underline" data-id="element-6442">
                terms of service
              </a>
              .
            </p>
          </div>

          <div className="p-6 border-t border-neutral-100 sm:border-none sm:p-0 sm:pb-10 sm:px-10 flex flex-col-reverse sm:flex-row items-center sm:justify-center gap-3 bg-white sticky bottom-0 z-10" data-id="element-6443">
            <button onClick={() => setStep(1)} className="w-full sm:w-auto px-6 py-3.5 sm:py-3 rounded-xl border border-neutral-300 text-[#2B211A] font-bold hover:bg-neutral-50 transition-colors" data-id="element-6444">
              Cancel
            </button>
            <button onClick={() => setStep(3)} className="w-full sm:w-auto px-6 py-3.5 sm:py-3 rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold transition-colors shadow-sm" data-id="element-6445">
              Accept and Continue
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // Step 3: Slack Permissions
  if (step === 3) {
    const viewItems = [{
      icon: <MessageSquare size={18} className="text-neutral-500" data-id="element-6446" />,
      label: 'Content and info about channels & conversations'
    }, {
      icon: <LayoutGrid size={18} className="text-neutral-500" data-id="element-6447" />,
      label: 'Content and info about your workspace'
    }];
    const actionItems = [{
      icon: <MessageSquare size={18} className="text-neutral-500" data-id="element-6448" />,
      label: 'Perform actions in channels & conversations'
    }];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-6449">
        {/* Thin top bar */}
        <div className="h-2 w-full bg-[#2B211A]" data-id="element-6450" />

        {/* Slack header */}
        <header className="w-full border-b border-neutral-200 px-6 sm:px-10 py-5" data-id="element-6451">
          <div className="flex items-center gap-2" data-id="element-6452">
            <SlackIcon size={28} data-id="element-6453" />
            <span className="text-[22px] font-bold text-[#333333] tracking-tight" data-id="element-6454">
              slack
            </span>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 w-full max-w-5xl mx-auto px-6 sm:px-10 py-8 sm:py-16 pb-32 sm:pb-16" data-id="element-6455">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16" data-id="element-6456">
            {/* Left column */}
            <div data-id="element-6457">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-sm mb-5" data-id="element-6458">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="Giga" className="w-7 h-7 sm:w-8 sm:h-8 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6459" />
              </div>

              <h1 className="text-2xl sm:text-[32px] leading-tight font-title font-bold text-[#333333] mb-4" data-id="element-6460">
                Allow the "Giga" app to access Slack
              </h1>

              <div className="flex items-center gap-2 text-[#2B211A] font-bold text-sm sm:text-[15px] mb-2" data-id="element-6461">
                <CheckCircle size={18} className="text-[#2B211A]" data-id="element-6462" />
                App is approved by Slack
              </div>
              <p className="text-sm sm:text-[15px] text-neutral-500 leading-relaxed max-w-md mb-8" data-id="element-6463">
                Apps are reviewed for quality before they are listed in the
                Slack Marketplace.{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-6464">
                  Learn more about app reviews.
                </a>
              </p>

              <p className="text-sm sm:text-[15px] font-bold text-[#333333] mb-2" data-id="element-6465">
                Workspace
              </p>
              <button type="button" className="w-full max-w-md flex items-center justify-between gap-3 px-4 py-3.5 rounded-lg border border-neutral-300 bg-white hover:border-neutral-400 transition-colors text-left" data-id="element-6466">
                <div className="flex items-center gap-3 min-w-0" data-id="element-6467">
                  <div className="w-7 h-7 rounded-md bg-[#4A90D9] flex items-center justify-center text-white text-[12px] font-bold shrink-0" data-id="element-6468">
                    T
                  </div>
                  <span className="font-bold text-[#333333] text-[16px] truncate" data-id="element-6469">
                    Tonic
                  </span>
                </div>
                <ChevronDown size={18} className="text-neutral-500 shrink-0" data-id="element-6470" />
              </button>
            </div>

            {/* Right column */}
            <div data-id="element-6471">
              <h2 className="text-lg sm:text-[20px] font-bold text-[#333333] mb-4 sm:mb-5" data-id="element-6472">
                Review app permissions
              </h2>

              <div className="rounded-2xl bg-[#F8F8F8] border border-neutral-200/70 p-5 sm:p-6 space-y-6 sm:space-y-7" data-id="element-6473">
                <div data-id="element-6474">
                  <p className="text-sm font-bold text-[#333333] mb-3" data-id="element-6475">
                    Information "Giga" can view
                  </p>
                  <div className="divide-y divide-neutral-200/70" data-id="element-6476">
                    {viewItems.map((item, i) => <div key={i} className="flex items-center gap-3 sm:gap-4 py-3 sm:py-4 first:pt-0" data-id="element-6477">
                        <span className="shrink-0" data-id="element-6478">{item.icon}</span>
                        <span className="flex-1 text-sm sm:text-[15px] text-[#333333] leading-snug" data-id="element-6479">
                          {item.label}
                        </span>
                        <ChevronRight size={16} className="text-neutral-400 shrink-0" data-id="element-6480" />
                      </div>)}
                  </div>
                </div>

                <div data-id="element-6481">
                  <p className="text-sm font-bold text-[#333333] mb-3" data-id="element-6482">
                    Actions "Giga" can take
                  </p>
                  <div className="divide-y divide-neutral-200/70" data-id="element-6483">
                    {actionItems.map((item, i) => <div key={i} className="flex items-center gap-3 sm:gap-4 py-3 sm:py-4 first:pt-0" data-id="element-6484">
                        <span className="shrink-0" data-id="element-6485">{item.icon}</span>
                        <span className="flex-1 text-sm sm:text-[15px] text-[#333333] leading-snug" data-id="element-6486">
                          {item.label}
                        </span>
                        <ChevronRight size={16} className="text-neutral-400 shrink-0" data-id="element-6487" />
                      </div>)}
                  </div>
                </div>
              </div>

              <p className="text-xs sm:text-[13px] text-neutral-500 leading-relaxed mt-5" data-id="element-6488">
                Slack will share your granted permissions with Giga. View
                Slack's{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-6489">
                  privacy policy
                </a>
                . By agreeing to allow Slack to share your granted permissions,
                you are also agreeing to Giga's{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-6490">
                  privacy agreement
                </a>{' '}
                and{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-6491">
                  terms of service
                </a>
                . Please review Giga's terms before installing.
              </p>

              <div className="fixed sm:static bottom-0 inset-x-0 p-4 sm:p-0 bg-white sm:bg-transparent border-t border-neutral-200 sm:border-none flex flex-col-reverse sm:flex-row items-center sm:justify-end gap-3 mt-0 sm:mt-6 z-10" data-id="element-6492">
                <button onClick={() => setStep(2)} className="w-full sm:w-auto px-6 py-3.5 sm:py-2.5 rounded-xl sm:rounded-lg border border-neutral-300 text-[#333333] text-[15px] font-bold hover:bg-neutral-50 transition-colors" data-id="element-6493">
                  Cancel
                </button>
                <button onClick={() => setStep(4)} className="w-full sm:w-auto px-7 py-3.5 sm:py-2.5 rounded-xl sm:rounded-lg bg-[#007A5A] hover:bg-[#148567] text-white text-[15px] font-bold transition-colors shadow-sm" data-id="element-6494">
                  Allow
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 4: Channel Selection
  if (step === 4) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-6495">
        <GigaHeader data-id="element-6496" />

        <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-6497">
          <div className="w-full max-w-md" data-id="element-6498">
            <div className="mb-7 sm:mb-10" data-id="element-6499">
              <div className="mb-2.5" data-id="element-6500">
                <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-6501">
                  STEP 1 OF 4
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-6502">
                Which{' '}
                <span className="inline-flex items-center align-middle -mt-1" data-id="element-6503">
                  <SlackIcon size={24} data-id="element-6504" />
                </span>{' '}
                channels should Giga join?
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed" data-id="element-6505">
                We found 10 public channels in your workspace
              </p>
            </div>

            <div className="space-y-3 mb-6" data-id="element-6506">
              <button onClick={() => setStep(5)} className="w-full py-3.5 px-6 rounded-xl bg-[#2B211A] text-white font-medium hover:bg-black transition-colors" data-id="element-6507">
                Invite Giga to public channels only
              </button>
              <button onClick={() => setStep(12)} className="w-full py-3.5 px-6 rounded-xl border-2 border-neutral-200 text-[#333333] font-medium hover:border-[#EA580C] hover:bg-[#FAF8F4]/30 transition-all" data-id="element-6508">
                Select channels
              </button>
            </div>

            <div className="flex items-center gap-2.5 text-[13px] text-neutral-500 bg-neutral-50 rounded-xl px-4 py-3" data-id="element-6509">
              <Info size={16} className="shrink-0" data-id="element-6510" />
              <p className="[text-wrap:balance]" data-id="element-6511">
                You can remove Giga from any channel anytime.
              </p>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 12: Specific Channel Selection
  if (step === 12) {
    const DUMMY_CHANNELS = ['all-faith-test', 'new-channel', 'social'];
    // Auto-select all by default to match the screenshot's "3 selected" state
    // if the user hasn't interacted yet.
    const activeChannels = selectedChannels.length > 0 || !selectedChannels.includes('initialized') ? selectedChannels.includes('initialized') ? selectedChannels.filter(c => c !== 'initialized') : DUMMY_CHANNELS : [];
    const toggleChannel = (ch: string) => {
      let next = [...activeChannels];
      if (next.includes(ch)) {
        next = next.filter(c => c !== ch);
      } else {
        next.push(ch);
      }
      // Add a hidden flag so we know the user has interacted and we shouldn't auto-fill
      if (!next.includes('initialized')) next.push('initialized');
      setSelectedChannels(next);
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-6512">
        <GigaHeader data-id="element-6513" />

        <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-6514">
          <div className="w-full max-w-md" data-id="element-6515">
            <div className="mb-7 sm:mb-10" data-id="element-6516">
              <div className="mb-2.5" data-id="element-6517">
                <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-6518">
                  STEP 1 OF 4
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-6519">
                Which{' '}
                <span className="inline-flex items-center align-middle -mt-1" data-id="element-6520">
                  <SlackIcon size={24} data-id="element-6521" />
                </span>{' '}
                channels should Giga join?
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed" data-id="element-6522">
                We found 10 public channels in your workspace
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden mb-4" data-id="element-6523">
              {/* Search */}
              <div className="relative border-b border-neutral-200" data-id="element-6524">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-6525" />
                <input type="text" placeholder={`Search ${WORKSPACE_NAME} workspace`} className="w-full pl-11 pr-4 py-4 text-[15px] text-[#2B211A] placeholder:text-neutral-400 focus:outline-none bg-transparent" data-id="element-6526" />
              </div>

              {/* List */}
              <div className="bg-[#FAF8F4]/50 p-2" data-id="element-6527">
                <div className="px-3 py-2 text-sm font-medium text-[#2B211A]" data-id="element-6528">
                  {activeChannels.length} selected
                </div>
                <div className="flex flex-col gap-1 pb-2" data-id="element-6529">
                  {DUMMY_CHANNELS.map(ch => {
                  const isSelected = activeChannels.includes(ch);
                  return <button key={ch} onClick={() => toggleChannel(ch)} className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg hover:bg-white transition-colors group" data-id="element-6530">
                        <div className="flex items-center gap-3 text-[15px] text-[#2B211A]" data-id="element-6531">
                          <Hash size={16} className="text-neutral-400" data-id="element-6532" />
                          {ch}
                        </div>
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'bg-[#EA580C] border-[#EA580C] text-white' : 'border-neutral-300 text-transparent group-hover:border-neutral-400'}`} data-id="element-6533">
                          <Check size={12} strokeWidth={3} data-id="element-6534" />
                        </div>
                      </button>;
                })}
                </div>
              </div>
            </div>

            <button onClick={() => setStep(5)} disabled={activeChannels.length === 0} className="w-full py-4 px-6 rounded-xl bg-[#2B211A] text-white font-medium hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4 shadow-sm" data-id="element-6535">
              Invite Giga to {activeChannels.length} channels
            </button>

            <div className="flex items-center justify-center gap-2 text-[13px] text-neutral-500" data-id="element-6536">
              <Info size={14} data-id="element-6537" />
              <p data-id="element-6538">You can remove Giga from any channel anytime</p>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 5: Tell us about you
  if (step === 5) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-6539">
        <GigaHeader data-id="element-6540" />

        <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-6541">
          <div className="w-full max-w-md" data-id="element-6542">
            <div className="mb-7 sm:mb-10" data-id="element-6543">
              <div className="mb-2.5" data-id="element-6544">
                <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-6545">
                  STEP 2 OF 4
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-6546">
                Tell us more about you
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed" data-id="element-6547">
                A couple quick questions so Giga can hit the ground running.
              </p>
            </div>

            <div className="space-y-6 mb-8" data-id="element-6548">
              <div data-id="element-6549">
                <label className="block text-sm font-medium text-[#333333] mb-2" data-id="element-6550">
                  What best describes you?
                </label>
                <SearchableSelect value={role} onChange={setRole} searchable={false} options={['I am a founder or executive and wear many hats at my company', 'I am a real estate agent or real estate broker', 'I am a consultant or work with many different clients', 'I work in sales or rev ops', 'I work on a marketing or growth team', 'I work in finance or operations', 'I am a content creator or social media influencer', 'I am a product manager or work with an engineering team', 'Other']} placeholder="Select an option" ariaLabel="What best describes you?" data-id="element-6551" />
              </div>

              <div data-id="element-6552">
                <label className="block text-sm font-medium text-[#333333] mb-3" data-id="element-6553">
                  What kind of business is it?
                </label>
                <SearchableSelect value={businessType} onChange={setBusinessType} options={BUSINESS_TYPES} placeholder="Select an option" searchPlaceholder="Search business types…" ariaLabel="What kind of business is it?" data-id="element-6554" />
              </div>

              <div data-id="element-6555">
                <label className="block text-sm font-medium text-[#333333] mb-2" data-id="element-6556">
                  What would Giga help you with? Select from use cases below:
                </label>
                <MultiSelectDropdown values={firstTeammates} onChange={toggleFirstTeammate} options={['Marketing & Ad Campaigns', 'Operations & Workflow Automation', 'Data analysis & reporting', 'Sales & CRM', 'Content & Creative Production', 'Engineering & Development', 'Executive / Leadership Assistant', 'Agency/Consultancy & Client Operations', 'eCommerce & Shopify', 'Other']} placeholder="Select use cases" data-id="element-6557" />
              </div>

              <div data-id="element-6558">
                <label className="block text-sm font-medium text-[#333333] mb-2" data-id="element-6559">
                  How did you find us?{' '}
                  <span className="text-neutral-400 font-normal" data-id="element-6560">
                    (optional)
                  </span>
                </label>
                <div className="relative" data-id="element-6561">
                  <select value={foundUs} onChange={e => setFoundUs(e.target.value)} className={`w-full px-4 py-3 rounded-xl border border-neutral-200 appearance-none bg-white focus:outline-none focus:border-[#EA580C] transition-colors ${foundUs ? 'text-[#333333]' : 'text-neutral-400'}`} data-id="element-6562">
                    <option value="" disabled data-id="element-6563">
                      Select an option
                    </option>
                    <option data-id="element-6564">Google search</option>
                    <option data-id="element-6565">Slack Marketplace</option>
                    <option data-id="element-6566">Community</option>
                    <option data-id="element-6567">Podcast</option>
                    <option data-id="element-6568">Reddit</option>
                    <option data-id="element-6569">Product Hunt</option>
                    <option data-id="element-6570">Facebook</option>
                    <option data-id="element-6571">YouTube</option>
                    <option data-id="element-6572">Twitter / X</option>
                    <option data-id="element-6573">Newsletter</option>
                    <option data-id="element-6574">Other</option>
                  </select>
                  <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" data-id="element-6575" />
                </div>
              </div>

              <div data-id="element-6576">
                <label className="block text-sm font-medium text-[#333333] mb-3" data-id="element-6577">
                  Keep me posted with Giga tips and updates
                </label>
                <div className="flex items-center gap-4" data-id="element-6578">
                  <button onClick={() => setEmailOptIn(true)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${emailOptIn ? 'text-[#EA580C]' : 'text-neutral-500 hover:text-[#333333]'}`} data-id="element-6579">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${emailOptIn ? 'border-[#EA580C] bg-[#EA580C]' : 'border-neutral-300'}`} data-id="element-6580">
                      {emailOptIn && <div className="w-2 h-2 bg-white rounded-full" data-id="element-6581"></div>}
                    </div>
                    <span className="text-sm font-medium" data-id="element-6582">Of course!</span>
                  </button>
                  <button onClick={() => setEmailOptIn(false)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${!emailOptIn ? 'text-[#EA580C]' : 'text-neutral-500 hover:text-[#333333]'}`} data-id="element-6583">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${!emailOptIn ? 'border-[#EA580C] bg-[#EA580C]' : 'border-neutral-300'}`} data-id="element-6584">
                      {!emailOptIn && <div className="w-2 h-2 bg-white rounded-full" data-id="element-6585"></div>}
                    </div>
                    <span className="text-sm font-medium" data-id="element-6586">No thanks</span>
                  </button>
                </div>
              </div>
            </div>

            <button onClick={() => setStep(6)} disabled={!role || !businessType || firstTeammates.length === 0} className={`w-full py-4 px-6 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${role && businessType && firstTeammates.length > 0 ? 'bg-[#2B211A] text-white hover:bg-black' : 'bg-neutral-300 text-white cursor-not-allowed'}`} data-id="element-6587">
              Continue
              <ArrowRight size={18} data-id="element-6588" />
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // Step 6: Tool Selection
  if (step === 6) {
    const CATEGORIES: {
      key: string;
      label: string;
    }[] = [{
      key: 'communication',
      label: 'COMMUNICATION'
    }, {
      key: 'docs',
      label: 'DOCS & DRIVE'
    }, {
      key: 'engineering',
      label: 'ENGINEERING'
    }, {
      key: 'project',
      label: 'PROJECT MANAGEMENT'
    }, {
      key: 'crm',
      label: 'CRM & SALES'
    }, {
      key: 'support',
      label: 'SUPPORT'
    }, {
      key: 'finance',
      label: 'FINANCE'
    }, {
      key: 'design',
      label: 'DESIGN'
    }, {
      key: 'analytics',
      label: 'ANALYTICS'
    }, {
      key: 'marketing',
      label: 'MARKETING'
    }];
    const filteredTools = ALL_TOOLS.filter(t => t.name.toLowerCase().includes(toolSearch.toLowerCase()));
    const POPULAR_TOOL_IDS = ['slack', 'gmail', 'googledrive', 'notion', 'github', 'linear', 'jira', 'salesforce', 'hubspot', 'stripe'];
    const popularTools = POPULAR_TOOL_IDS.map(id => ALL_TOOLS.find(t => t.id === id)).filter(Boolean) as typeof ALL_TOOLS;
    const isSearching = toolSearch.trim() !== '';
    const renderToolCard = (tool: (typeof ALL_TOOLS)[number]) => {
      const isSelected = selectedTools.includes(tool.id);
      return <button key={tool.id} onClick={() => toggleTool(tool.id)} aria-pressed={isSelected} className={`group relative p-3 rounded-2xl border transition-all duration-200 ease-out active:scale-[0.97] ${isSelected ? 'border-[#F8C9A4] bg-[#FFF7ED] shadow-[0_1px_3px_rgba(234,88,12,0.08)]' : 'border-neutral-200/80 bg-white hover:border-[#F3D2BB] hover:bg-[#FFFBF7]'}`} data-id="element-6589">
          <AnimatePresence data-id="element-6590">
            {isSelected && <motion.div initial={{
            opacity: 0,
            scale: 0.5
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.5
          }} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} className="absolute top-1.5 right-1.5 w-[18px] h-[18px] bg-[#EA580C] rounded-full flex items-center justify-center shadow-[0_1px_2px_rgba(234,88,12,0.3)]" data-id="element-6591">
                <Check size={11} strokeWidth={2.25} className="text-white" data-id="element-6592" />
              </motion.div>}
          </AnimatePresence>
          <ToolLogo name={tool.name} icon={tool.icon} className="w-8 h-8 mx-auto mb-1.5 transition-transform duration-200 group-hover:scale-105" fallbackTextSize="text-sm" data-id="element-6593" />
          <div className="text-xs font-medium text-[#333333] text-center leading-tight" data-id="element-6594">
            {tool.name}
          </div>
        </button>;
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-6595">
        <GigaHeader hideOnMobile data-id="element-6596" />

        <div className="flex-1 px-6 pt-6 lg:pt-12 pb-36 overflow-auto" data-id="element-6597">
          <div className="max-w-4xl mx-auto" data-id="element-6598">
            <div className="mb-2.5" data-id="element-6599">
              <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-6600">
                STEP 3 OF 4
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-6601">
              Where does your work actually live?
            </h1>
            <p className="text-sm sm:text-base text-neutral-600 mb-6 sm:mb-8 leading-relaxed" data-id="element-6602">
              GitHub, Linear, the docs you reference — pick everything. Giga
              reads across them with citations back to source.
            </p>

            <div className="relative mb-6 sm:mb-8" data-id="element-6603">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" size={20} data-id="element-6604" />
              <input type="text" placeholder="Search tools..." value={toolSearch} onChange={e => setToolSearch(e.target.value)} className="w-full pl-12 pr-4 py-3 sm:py-3.5 rounded-xl border border-neutral-200 text-[#333333] bg-white focus:outline-none focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/15 transition-all" data-id="element-6605" />
            </div>

            <div className="relative inline-block mb-7 sm:mb-10" data-id="element-6606">
              <button onClick={() => setRoleMenuOpen(!roleMenuOpen)} aria-expanded={roleMenuOpen} aria-label={`Suggested for ${funcRoles.join(', ')}. Change use cases`} className="group inline-flex items-center gap-2 pl-3.5 pr-3 py-2 rounded-full border border-neutral-200 bg-white text-sm shadow-[0_1px_2px_rgba(0,0,0,0.03)] hover:bg-neutral-50 hover:border-neutral-300 transition-colors" data-id="element-6607">
                <Sparkles size={14} className="text-neutral-400 shrink-0" data-id="element-6608" />
                <span className="text-neutral-500" data-id="element-6609">Suggested for</span>
                <span className="font-semibold text-[#333333] max-w-[200px] truncate" data-id="element-6610">
                  {funcRoles.length === 0 ? 'Select use cases' : funcRoles.length <= 1 ? funcRoles[0] : `${funcRoles.length} use cases`}
                </span>
                <ChevronDown size={15} className={`text-neutral-400 transition-transform ${roleMenuOpen ? 'rotate-180' : ''}`} data-id="element-6611" />
              </button>

              <AnimatePresence data-id="element-6612">
                {roleMenuOpen && <>
                    <div className="fixed inset-0 z-40" onClick={() => setRoleMenuOpen(false)} data-id="element-6613" />
                    <motion.div initial={{
                  opacity: 0,
                  y: 4,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 4,
                  scale: 0.95
                }} transition={{
                  duration: 0.15
                }} className="absolute left-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-neutral-200 py-2 z-50 max-h-80 overflow-y-auto" data-id="element-6614">
                      {Object.keys(USE_CASE_RECS).map(r => {
                    const isSelected = funcRoles.includes(r);
                    return <button key={r} onClick={e => {
                      e.preventDefault();
                      setFuncRoles(prev => {
                        const next = isSelected ? prev.filter(x => x !== r) : [...prev, r];
                        const newTools = new Set<string>();
                        next.forEach(role => {
                          USE_CASE_RECS[role]?.forEach(t => newTools.add(t));
                        });
                        setSelectedTools(Array.from(newTools));
                        return next;
                      });
                    }} className={`w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm transition-colors ${isSelected ? 'bg-[#FFEDD5] text-[#333333] font-medium' : 'hover:bg-neutral-50 text-neutral-600'}`} data-id="element-6615">
                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-[#EA580C] border-[#EA580C]' : 'border-neutral-300 bg-white'}`} data-id="element-6616">
                              {isSelected && <Check size={12} strokeWidth={3} className="text-white" data-id="element-6617" />}
                            </div>
                            <span className="flex-1 leading-snug" data-id="element-6618">{r}</span>
                          </button>;
                  })}
                    </motion.div>
                  </>}
              </AnimatePresence>
            </div>

            {!isSearching && popularTools.length > 0 && <div className="mb-5 sm:mb-6" data-id="element-6619">
                <h2 className="flex items-center gap-1.5 text-[11px] sm:text-xs font-bold text-[#EA580C] uppercase tracking-wider mb-2.5 sm:mb-3" data-id="element-6620">
                  <Sparkles size={13} className="shrink-0" data-id="element-6621" />
                  Most popular
                </h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5" data-id="element-6622">
                  {popularTools.map(tool => renderToolCard(tool))}
                </div>
              </div>}

            {CATEGORIES.map(cat => {
            const items = filteredTools.filter(t => t.category === cat.key);
            if (items.length === 0) return null;
            return <div key={cat.key} className="mb-5 sm:mb-6" data-id="element-6623">
                  <h2 className="text-[11px] sm:text-xs font-bold text-neutral-400 uppercase tracking-wider mb-2.5 sm:mb-3" data-id="element-6624">
                    {cat.label}
                  </h2>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5" data-id="element-6625">
                    {items.map(tool => renderToolCard(tool))}
                  </div>
                </div>;
          })}

            {filteredTools.length === 0 && (() => {
            const term = toolSearch.trim();
            const requested = term !== '' && requestedTools.includes(term.toLowerCase());
            return <div className="text-center py-12 px-6 rounded-2xl border-2 border-dashed border-neutral-200 bg-[#FAF8F4]" data-id="element-6626">
                    {requested ? <div className="max-w-md mx-auto" data-id="element-6627">
                        <div className="w-12 h-12 rounded-full bg-[#FFEDD5] flex items-center justify-center mx-auto mb-4" data-id="element-6628">
                          <Check size={22} className="text-[#EA580C]" data-id="element-6629" />
                        </div>
                        <h3 className="font-bold text-[#333333] text-lg mb-1" data-id="element-6630">
                          On it
                        </h3>
                        <p className="text-neutral-600 text-sm" data-id="element-6631">
                          We&rsquo;ve got your request for{' '}
                          <span className="font-semibold" data-id="element-6632">{term}</span>
                          {requestedToolLink ? ' and the link' : ''}. If it has
                          an API or a login page, our team usually wires it up{' '}
                          <span className="font-semibold text-[#333333]" data-id="element-6633">
                            within 1 hour
                          </span>{' '}
                          — we&rsquo;ll email you the moment it&rsquo;s live.
                        </p>
                      </div> : <div className="max-w-xl mx-auto text-left" data-id="element-6634">
                        <h3 className="font-bold text-[#333333] text-lg mb-1 text-center" data-id="element-6635">
                          {term ? `Don't see ${term}?` : 'Looking for something specific?'}
                        </h3>
                        <p className="text-neutral-600 text-sm mb-5 text-center" data-id="element-6636">
                          Giga connects to virtually anything. Tell us what you
                          need — drop a link and we&rsquo;ll usually have it
                          connected within 1 hour.
                        </p>
                        <label className="block text-[13px] font-semibold text-[#333333] mb-1.5" data-id="element-6637">
                          Link{' '}
                          <span className="font-normal text-neutral-400" data-id="element-6638">
                            (optional)
                          </span>
                        </label>
                        <input type="url" value={requestedToolLink} onChange={e => setRequestedToolLink(e.target.value)} placeholder="https://yourtool.com" className="w-full mb-4 px-4 py-2.5 rounded-xl border border-neutral-200 bg-white text-sm text-[#333333] placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#333333]/20 focus:border-[#333333]" data-id="element-6639" />
                        <button onClick={() => term && setRequestedTools(prev => [...prev, term.toLowerCase()])} disabled={!term} className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold transition-colors ${term ? 'bg-[#2B211A] text-white hover:bg-black' : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'}`} data-id="element-6640">
                          {term ? `Request ${term}` : 'Type a name to request it'}
                        </button>
                        <p className="flex items-start justify-center gap-1.5 text-xs text-neutral-400 leading-relaxed text-center mt-4" data-id="element-6641">
                          <Info size={13} className="shrink-0 mt-0.5" data-id="element-6642" />
                          <span data-id="element-6643">
                            Connect anything later from Settings — API token,
                            MCP server, or OAuth.
                          </span>
                        </p>
                      </div>}
                  </div>;
          })()}

            <StickyRevealBar data-id="element-6644">
              <div className="flex items-center justify-between gap-3" data-id="element-6645">
                <button onClick={() => setStep(5)} aria-label="Back" className="flex items-center justify-center w-11 h-11 rounded-full text-neutral-500 hover:text-[#333333] hover:bg-neutral-100 transition-colors shrink-0" data-id="element-6646">
                  <ArrowLeft size={20} data-id="element-6647" />
                </button>
                <div className="flex items-center gap-2 sm:gap-4" data-id="element-6648">
                  <button onClick={() => setStep(7)} className="px-2 sm:px-4 py-3 text-sm font-medium text-neutral-500 hover:text-[#333333] transition-colors whitespace-nowrap" data-id="element-6649">
                    Later
                  </button>
                  <button onClick={() => setStep(7)} aria-label={selectedTools.length > 0 ? `Continue with ${selectedTools.length} tools` : 'Continue'} className="px-5 sm:px-7 py-3 rounded-xl bg-[#2B211A] text-white font-medium hover:bg-black transition-colors flex items-center gap-2 whitespace-nowrap" data-id="element-6650">
                    Continue
                    {selectedTools.length > 0 && <span className="hidden sm:inline text-sm" data-id="element-6651">
                        with {selectedTools.length}{' '}
                        {selectedTools.length === 1 ? 'tool' : 'tools'}
                      </span>}
                    <ArrowRight size={18} strokeWidth={2} data-id="element-6652" />
                  </button>
                </div>
              </div>
            </StickyRevealBar>
          </div>
        </div>
      </motion.div>;
  }
  // Step 7: Connections — per-tool OAuth permission flow
  if (step === 7) {
    const connections = step7Connections;
    const total = connections.length;
    const doneCount = connectedConns.length;
    const activeIdx = step7ActiveIdx;
    const allDone = step7AllDone;
    const allow = (conn: any) => {
      const next = connectedConns.includes(conn.id) ? connectedConns : [...connectedConns, conn.id];
      setConnectedConns(next);
      // Lock in the chosen visibility (default to shared with team if untouched)
      setConnVisibility(prev => ({
        ...prev,
        [conn.id]: prev[conn.id] ?? 'team'
      }));
      // Reset optional permission toggles for the next tool's fresh card
      setOptionalScopes({});
      if (next.length === total) {
        // All done — close the consent screen and advance.
        setConnectPhase(null);
        setPermModal(null);
        setTimeout(() => setStep(8), 600);
      } else {
        // Visibility was set up front on the list, so once the user kicks off
        // the sequence we chain straight to the next tool's consent screen.
        const nextConn = connections.find(c => !next.includes(c.id));
        if (nextConn) {
          setPermModal(nextConn);
          setConnectPhase('consent');
        } else {
          setConnectPhase(null);
          setPermModal(null);
        }
      }
    };
    const handleAllowClick = (conn: any) => {
      setConnectPhase('handing-off');
      setTimeout(() => {
        allow(conn);
      }, 2000);
    };
    const startConnecting = () => {
      const firstConn = connections.find(c => !connectedConns.includes(c.id));
      if (!firstConn) return;
      setConnectionsStarted(true);
      setPermModal(firstConn);
      setConnectPhase('consent');
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-6653">
        <GigaHeader hideOnMobile data-id="element-6654" />

        <div className="flex-1 px-4 sm:px-6 py-6 sm:py-12 pb-40 overflow-auto" data-id="element-6655">
          <div className="max-w-4xl mx-auto" data-id="element-6656">
            <div className="mb-2.5" data-id="element-6657">
              <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-6658">
                STEP 4 OF 4
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-6659">
              Let's plug Giga into your stack.
            </h1>
            <p className="text-sm sm:text-base text-neutral-600 mb-6 sm:mb-8 leading-relaxed" data-id="element-6660">
              Approve each one to connect. You can adjust permissions anytime in
              Settings.
            </p>

            <div className="bg-neutral-50 rounded-2xl p-4 sm:p-6 mb-6" data-id="element-6661">
              <div className="flex items-center justify-between mb-4 sm:mb-5" data-id="element-6662">
                <h2 className="text-lg sm:text-xl md:text-2xl font-title font-bold text-[#333333] italic" data-id="element-6663">
                  {total} connections to make
                </h2>
                <span className="text-xs sm:text-sm font-medium text-neutral-500" data-id="element-6664">
                  {doneCount}/{total} done
                </span>
              </div>

              {/* progress bar */}
              <div className="h-1.5 w-full rounded-full bg-neutral-200 mb-5 sm:mb-6 overflow-hidden" data-id="element-6665">
                <motion.div className="h-full rounded-full bg-[#EA580C]" animate={{
                width: `${doneCount / total * 100}%`
              }} transition={{
                type: 'spring',
                stiffness: 120,
                damping: 20
              }} data-id="element-6666" />
              </div>

              <div className="space-y-2" data-id="element-6667">
                {connections.map(conn => {
                const connected = connectedConns.includes(conn.id);
                return <div key={conn.id} className={`flex items-center gap-3 p-3 sm:p-3.5 rounded-xl bg-white border transition-all ${connected ? 'border-[#EA580C]' : 'border-neutral-200'}`} data-id="element-6668">
                      {/* Tool info */}
                      <ToolLogo name={conn.name} icon={conn.icon} className="w-9 h-9 shrink-0" fallbackTextSize="text-sm" data-id="element-6669" />
                      <div className="min-w-0 flex-1" data-id="element-6670">
                        <div className="font-bold text-sm sm:text-base text-[#333333] truncate" data-id="element-6671">
                          {conn.name}
                        </div>
                        <div className="text-xs text-neutral-500" data-id="element-6672">
                          {conn.scopes.length} permission
                          {conn.scopes.length > 1 ? 's' : ''}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="shrink-0" data-id="element-6673">
                        {connected ? <span className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full bg-[#FFF0E6] text-xs font-semibold text-[#EA580C]" data-id="element-6674">
                            <span className="w-4 h-4 rounded-full bg-[#EA580C] flex items-center justify-center" data-id="element-6675">
                              <Check size={11} strokeWidth={3} className="text-white" data-id="element-6676" />
                            </span>
                            Connected
                          </span> : <span className="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full border border-neutral-200 bg-white text-xs font-medium text-neutral-500" data-id="element-6677">
                            <span className="w-2 h-2 rounded-full border-[1.5px] border-neutral-300" data-id="element-6678" />
                            Pending
                          </span>}
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed bottom action bar — keeps the primary action reachable as the
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                           tool list scrolls. Hidden while a consent screen is open. */}
        {!permModal && <StickyRevealBar data-id="element-6679">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3" data-id="element-6680">
              {allDone ? <button onClick={() => setStep(11)} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-[#2B211A] text-white font-bold hover:bg-black transition-colors shrink-0" data-id="element-6681">
                  Continue
                  <ArrowRight size={18} data-id="element-6682" />
                </button> : <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 shrink-0" data-id="element-6683">
                  <button onClick={() => setStep(11)} className="w-full sm:w-auto px-4 py-3 rounded-xl text-sm font-semibold text-neutral-500 hover:text-[#333333] hover:bg-neutral-100 transition-colors" data-id="element-6684">
                    {doneCount > 0 ? 'Finish the rest later' : "I'll connect later"}
                  </button>
                  <button onClick={startConnecting} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-[#2B211A] text-white font-bold hover:bg-black transition-colors" data-id="element-6685">
                    {doneCount > 0 ? 'Resume connecting' : 'Start connecting'}
                    <ArrowRight size={18} data-id="element-6686" />
                  </button>
                </div>}
            </div>
          </StickyRevealBar>}

        {/* OAuth permission modal / full-screen flow — sits OUTSIDE the page
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                           wrapper concerns; full-screen cover. */}
        <AnimatePresence data-id="element-6687">
          {permModal && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-50 flex flex-col items-center bg-white md:bg-[#F5F5F5] overflow-y-auto" data-id="element-6688">
              {connectPhase === 'consent' && <div className="w-full h-full flex flex-col items-center md:pt-12 md:pb-24 md:px-6" data-id="element-6689">
                  {/* Fake URL Bar - Full width header on mobile, floating pill on desktop */}
                  <div className="flex items-center justify-between w-full md:max-w-md bg-neutral-100 md:bg-white md:rounded-full px-4 py-3 md:py-2 md:shadow-sm border-b md:border border-neutral-200 mb-0 md:mb-8 shrink-0" data-id="element-6690">
                    <div className="flex items-center gap-2 text-neutral-500" data-id="element-6691">
                      <Lock size={14} className="text-[#EA580C]" data-id="element-6692" />
                      <span className="font-mono text-xs truncate max-w-[200px] sm:max-w-none" data-id="element-6693">
                        {permModal.id}.com{' '}
                        <span className="text-neutral-400" data-id="element-6694">
                          /oauth/authorize
                        </span>
                      </span>
                    </div>
                    <span className="font-mono text-xs text-neutral-400 shrink-0" data-id="element-6695">
                      {doneCount + 1} of {total}
                    </span>
                  </div>

                  {/* Consent Card - Full width on mobile, rounded card on desktop */}
                  <div className="w-full max-w-md bg-white md:rounded-3xl md:shadow-xl md:border border-neutral-200 flex flex-col min-h-screen md:min-h-0" data-id="element-6696">
                    <div className="flex-1 overflow-auto" data-id="element-6697">
                      <div className="p-6 md:p-8 pb-6 flex flex-col items-center" data-id="element-6698">
                        <div className="flex items-center justify-center gap-4 mb-6" data-id="element-6699">
                          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl border border-neutral-200 flex items-center justify-center shadow-sm bg-white" data-id="element-6700">
                            <ToolLogo name={permModal.name} icon={permModal.icon} className="w-7 h-7 md:w-8 md:h-8" data-id="element-6701" />
                          </div>
                          <ArrowRight size={16} className="text-neutral-300" data-id="element-6702" />
                          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-sm" data-id="element-6703">
                            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="Giga" className="w-7 h-7 md:w-8 md:h-8 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6704" />
                          </div>
                        </div>

                        <h3 className="text-xl md:text-2xl font-title font-bold text-[#333333] text-center mb-3 leading-tight" data-id="element-6705">
                          Gigabrain is requesting access to your{' '}
                          {permModal.name} account
                        </h3>

                        <p className="text-[13px] md:text-[14px] text-neutral-500 text-center mb-6 max-w-sm leading-relaxed" data-id="element-6706">
                          Choose what Giga can do. You can change this anytime
                          in Settings → Connections, and revoke from{' '}
                          {permModal.name} directly.
                        </p>

                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-neutral-200 bg-neutral-50" data-id="element-6707">
                          <div className="w-5 h-5 rounded-full bg-[#2B211A] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-6708">
                            L
                          </div>
                          <span className="text-sm text-neutral-700" data-id="element-6709">
                            Signed in as{' '}
                            <span className="font-semibold" data-id="element-6710">l@acme.co</span>
                          </span>
                        </div>
                      </div>

                      <div className="px-6 md:px-8 py-6 bg-white border-t border-neutral-100" data-id="element-6711">
                        <div className="flex items-center justify-between mb-4" data-id="element-6712">
                          <span className="text-sm font-semibold text-[#333333]" data-id="element-6713">
                            This will allow Giga to:
                          </span>
                          <span className="text-xs font-mono text-neutral-400" data-id="element-6714">
                            {permModal.scopes.filter((s: any) => s.required || optionalScopes[s.id]).length}
                            /{permModal.scopes.length} permissions
                          </span>
                        </div>

                        <div className="space-y-0 divide-y divide-neutral-100 border-b border-neutral-100 mb-6" data-id="element-6715">
                          {permModal.scopes.map((scope: any) => <label key={scope.id} className={`flex items-start gap-4 py-4 ${scope.required ? 'cursor-default' : 'cursor-pointer'}`} data-id="element-6716">
                              <div className="mt-0.5" data-id="element-6717">
                                <div className={`w-5 h-5 rounded flex items-center justify-center ${scope.required || !!optionalScopes[scope.id] ? 'bg-[#2B211A] border-[#2B211A]' : 'border-neutral-300 border'} ${scope.required ? 'opacity-100' : ''}`} data-id="element-6718">
                                  {(scope.required || !!optionalScopes[scope.id]) && <Check size={12} className="text-white" strokeWidth={3} data-id="element-6719" />}
                                </div>
                              </div>
                              <div data-id="element-6720">
                                <div className="flex items-center gap-2 mb-0.5" data-id="element-6721">
                                  <span className="text-[15px] font-bold text-[#333333]" data-id="element-6722">
                                    {scope.label}
                                  </span>
                                  {scope.required && <span className="px-1.5 py-0.5 rounded bg-neutral-100 text-[10px] font-bold text-neutral-500 tracking-wide uppercase" data-id="element-6723">
                                      Required
                                    </span>}
                                </div>
                                <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-6724">
                                  {scope.description}
                                </p>
                              </div>
                            </label>)}
                        </div>

                        <div className="p-4 rounded-xl border border-neutral-200 bg-neutral-50 flex gap-3" data-id="element-6725">
                          <ShieldCheck className="w-5 h-5 text-[#EA580C] shrink-0 mt-0.5" data-id="element-6726" />
                          <p className="text-[13px] text-neutral-600 leading-relaxed" data-id="element-6727">
                            Giga stores access tokens encrypted at rest and
                            isolated per workspace. Your {permModal.name} data
                            is never shared with other Gigabrain customers or
                            used to train shared models.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 md:p-6 bg-white border-t border-neutral-100 flex flex-col md:flex-row items-center justify-between sticky bottom-0 z-10" data-id="element-6728">
                      <p className="hidden md:block text-[11px] text-neutral-500 max-w-[180px] leading-relaxed" data-id="element-6729">
                        By continuing, you agree to Gigabrain's{' '}
                        <a href="#" className="text-neutral-700 hover:underline font-medium" data-id="element-6730">
                          Terms
                        </a>{' '}
                        and{' '}
                        <a href="#" className="text-neutral-700 hover:underline font-medium" data-id="element-6731">
                          Privacy Notice
                        </a>
                        .
                      </p>
                      <div className="flex flex-col-reverse md:flex-row md:items-center gap-3 w-full md:w-auto" data-id="element-6732">
                        <button onClick={() => {
                    setPermModal(null);
                    setConnectPhase(null);
                    setConnectionsStarted(false);
                  }} className="w-full md:w-auto px-5 py-3.5 md:py-2.5 rounded-xl text-sm font-semibold text-neutral-600 hover:bg-neutral-100 transition-colors" data-id="element-6733">
                          Cancel
                        </button>
                        <button onClick={() => handleAllowClick(permModal)} className="w-full md:w-auto px-6 py-3.5 md:py-2.5 rounded-xl bg-[#2B211A] text-white text-sm font-semibold hover:bg-[#EA580C] transition-colors shadow-sm whitespace-nowrap" data-id="element-6734">
                          Allow access
                        </button>
                      </div>
                    </div>
                  </div>

                  <p className="hidden md:block mt-6 font-mono text-xs text-neutral-400 text-center max-w-sm leading-relaxed" data-id="element-6735">
                    You'll be returned to Gigabrain automatically. This stays in
                    the same tab.
                  </p>
                </div>}

              {connectPhase === 'handing-off' && <div className="flex-1 flex flex-col items-center justify-center w-full" data-id="element-6736">
                  <div className="flex items-center gap-6 mb-6" data-id="element-6737">
                    <div className="w-12 h-12 rounded-xl border border-neutral-200 bg-white flex items-center justify-center shadow-sm" data-id="element-6738">
                      <ToolLogo name={permModal.name} icon={permModal.icon} className="w-6 h-6" data-id="element-6739" />
                    </div>
                    <Loader2 size={24} className="text-neutral-400 animate-spin" data-id="element-6740" />
                    <div className="w-12 h-12 rounded-xl bg-white border border-neutral-200 flex items-center justify-center shadow-sm" data-id="element-6741">
                      <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="Giga" className="w-6 h-6 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-6742" />
                    </div>
                  </div>
                  <p className="font-mono text-sm text-neutral-500" data-id="element-6743">
                    Handing off to {permModal.name}...
                  </p>
                </div>}
            </motion.div>}
        </AnimatePresence>
      </motion.div>;
  }
  // Step 8: Now go to Slack
  if (step === 8) {
    const navItems = [{
      label: 'Threads',
      icon: MessageSquare
    }, {
      label: 'Huddles',
      icon: Headphones
    }, {
      label: 'Recap',
      icon: ArrowRight
    }, {
      label: 'Directories',
      icon: Users
    }];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-6744">
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 md:px-10 py-6" data-id="element-6745">
          <Link to="/" aria-label="Back to Giga home" className="flex items-center gap-2 group w-fit" data-id="element-6746">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain group-hover:scale-105 transition-transform" data-id="element-6747" />
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/giga4x.png" alt="Giga" className="h-6 object-contain" data-id="element-6748" />
          </Link>
        </div>

        <div className="flex-1 flex items-center" data-id="element-6749">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 w-full grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-10" data-id="element-6750">
            {/* Left — call to action */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-6751">
              <h1 className="text-5xl md:text-6xl font-title font-bold text-[#333333] mb-5 tracking-tight" data-id="element-6752">
                Giga's waiting in Slack.
              </h1>
              <p className="text-lg text-neutral-600 mb-8 max-w-md leading-relaxed" data-id="element-6753">
                Open Slack and find Giga under{' '}
                <span className="font-semibold text-[#333333]" data-id="element-6754">"Apps"</span>, or
                just <span className="font-semibold text-[#333333]" data-id="element-6755">@Giga</span>{' '}
                them in any channel in {WORKSPACE_NAME}.
              </p>
              <div className="flex flex-wrap items-center gap-4" data-id="element-6756">
                <button onClick={() => setStep(9)} className="inline-flex items-center justify-center gap-2.5 bg-[#2B211A] text-white px-7 py-4 rounded-xl font-bold text-base hover:bg-black transition-colors shadow-lg shadow-black/20" data-id="element-6757">
                  <SlackIcon size={20} data-id="element-6758" />
                  Message Giga
                </button>
                <Link to="/" className="inline-flex items-center gap-2 text-neutral-500 font-medium hover:text-[#333333] transition-colors" data-id="element-6759">
                  <ArrowLeft size={18} data-id="element-6760" />
                  Back to home
                </Link>
              </div>
            </motion.div>

            {/* Right — Slack window mock */}
            <motion.div initial={{
            opacity: 0,
            y: 30,
            scale: 0.97
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="hidden lg:block rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 bg-white" data-id="element-6761">
              {/* macOS chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-[#3F0E40]" data-id="element-6762">
                <span className="w-3 h-3 rounded-full bg-[#ff5f57]" data-id="element-6763" />
                <span className="w-3 h-3 rounded-full bg-[#febc2e]" data-id="element-6764" />
                <span className="w-3 h-3 rounded-full bg-[#28c840]" data-id="element-6765" />
                <div className="flex-1 mx-4" data-id="element-6766">
                  <div className="flex items-center gap-2 rounded-md bg-black/20 px-3 py-1.5 text-white/60 text-xs" data-id="element-6767">
                    <Search size={12} data-id="element-6768" />
                    Search
                  </div>
                </div>
              </div>

              <div className="flex h-[420px]" data-id="element-6769">
                {/* Sidebar */}
                <div className="w-40 shrink-0 bg-[#3F0E40] text-white/80 py-3 flex flex-col" data-id="element-6770">
                  <div className="px-4 pb-3 mb-1 border-b border-white/10 flex items-center gap-1.5 font-bold text-white text-sm" data-id="element-6771">
                    {WORKSPACE_NAME}
                    <ChevronDown size={14} data-id="element-6772" />
                  </div>
                  <div className="px-2 space-y-0.5 text-[13px]" data-id="element-6773">
                    {navItems.map(n => <div key={n.label} className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-6774">
                        <n.icon size={15} className="opacity-70" data-id="element-6775" />
                        {n.label}
                      </div>)}
                    <div className="h-px bg-white/10 my-2 mx-2" data-id="element-6776" />
                    <div className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-6777">
                      <Hash size={15} className="opacity-70" data-id="element-6778" />
                      Channels
                    </div>
                    <div className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-6779">
                      <MessageSquare size={15} className="opacity-70" data-id="element-6780" />
                      Direct messages
                    </div>
                    <div className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-6781">
                      <Grid3x3 size={15} className="opacity-70" data-id="element-6782" />
                      Apps
                    </div>
                    {/* Active Giga DM */}
                    <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-[#1164A3] text-white mt-0.5" data-id="element-6783">
                      <span className="flex items-center gap-2.5" data-id="element-6784">
                        <span className="w-4 h-4 rounded bg-[#F97316] flex items-center justify-center" data-id="element-6785">
                          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="" aria-hidden="true" className="w-2.5 h-2.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6786" />
                        </span>
                        Giga
                      </span>
                      <span className="w-4 h-4 rounded-full bg-[#E01E5A] text-white text-[10px] font-bold flex items-center justify-center" data-id="element-6787">
                        1
                      </span>
                    </div>
                  </div>
                </div>

                {/* Main column */}
                <div className="flex-1 min-w-0 bg-white flex flex-col" data-id="element-6788">
                  {/* DM header + tabs */}
                  <div className="px-4 pt-3 border-b border-neutral-200" data-id="element-6789">
                    <div className="flex items-center gap-2 mb-3" data-id="element-6790">
                      <span className="w-6 h-6 rounded bg-[#F97316] flex items-center justify-center" data-id="element-6791">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="" aria-hidden="true" className="w-3.5 h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6792" />
                      </span>
                      <span className="font-bold text-[#333333] text-sm" data-id="element-6793">
                        Giga
                      </span>
                      <ChevronDown size={14} className="text-neutral-400" data-id="element-6794" />
                    </div>
                    <div className="flex items-center gap-5 text-sm" data-id="element-6795">
                      <span className="pb-2 text-neutral-500" data-id="element-6796">Home</span>
                      <span className="pb-2 -mb-px border-b-2 border-[#333333] font-semibold text-[#333333]" data-id="element-6797">
                        Messages
                      </span>
                      <span className="pb-2 text-neutral-500" data-id="element-6798">About</span>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-auto px-4 py-4" data-id="element-6799">
                    <div className="text-center text-[11px] text-neutral-400 mb-4" data-id="element-6800">
                      This is the very beginning of your direct message history
                      with <span className="text-[#1164A3]" data-id="element-6801">@Giga</span>
                    </div>
                    <div className="flex items-start gap-2.5" data-id="element-6802">
                      <span className="w-9 h-9 rounded-lg bg-[#F97316] flex items-center justify-center shrink-0" data-id="element-6803">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6804" />
                      </span>
                      <div className="min-w-0" data-id="element-6805">
                        <div className="flex items-center gap-1.5 mb-0.5" data-id="element-6806">
                          <span className="font-bold text-sm text-[#333333]" data-id="element-6807">
                            Giga
                          </span>
                          <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-200 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-6808">
                            App
                          </span>
                          <span className="text-[11px] text-neutral-400 hover:underline cursor-pointer" data-id="element-6809">
                            9:16 AM
                          </span>
                        </div>
                        <p className="text-sm text-[#333333] leading-snug mb-2" data-id="element-6810">
                          Hi 👋, great to meet you. I'm Giga, your new AI
                          coworker. Here are two ways to work with me:
                        </p>
                        <p className="text-sm text-[#333333] leading-snug mb-1.5" data-id="element-6811">
                          💬 <span className="font-bold" data-id="element-6812">DM me here</span> —
                          just message me like a coworker. Research, analysis,
                          reports, automation.
                        </p>
                        <p className="text-sm text-[#333333] leading-snug" data-id="element-6813">
                          <span className="text-[#1164A3] font-medium" data-id="element-6814">
                            @Giga
                          </span>{' '}
                          <span className="font-bold" data-id="element-6815">in any channel</span> —
                          mention me in context and I'll jump in with the full
                          thread as background.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right — Slack mock (Mobile) */}
            <motion.div initial={{
            opacity: 0,
            y: 30,
            scale: 0.97
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="lg:hidden rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 bg-white flex flex-col w-full max-w-md mx-auto" data-id="element-6816">
              {/* Mobile Slack Header */}
              <div className="bg-[#3F0E40] text-white px-4 py-3 flex items-center gap-3" data-id="element-6817">
                <div className="w-9 h-9 rounded bg-white/20 flex items-center justify-center shrink-0" data-id="element-6818">
                  <span className="font-bold text-base" data-id="element-6819">
                    {WORKSPACE_INITIAL}
                  </span>
                </div>
                <div className="flex-1 min-w-0" data-id="element-6820">
                  <div className="text-xs font-medium text-white/70 truncate" data-id="element-6821">
                    {WORKSPACE_NAME}
                  </div>
                  <div className="font-bold text-[15px] truncate leading-tight" data-id="element-6822">
                    Giga
                  </div>
                </div>
                <Search size={20} className="text-white/80" data-id="element-6823" />
              </div>

              {/* Mobile Messages Area */}
              <div className="flex-1 bg-white p-5" data-id="element-6824">
                <div className="text-center text-[11px] text-neutral-400 mb-5" data-id="element-6825">
                  This is the very beginning of your direct message history with{' '}
                  <span className="text-[#1164A3]" data-id="element-6826">@Giga</span>
                </div>
                <div className="flex items-start gap-3" data-id="element-6827">
                  <span className="w-10 h-10 rounded-lg bg-[#F97316] flex items-center justify-center shrink-0" data-id="element-6828">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="" aria-hidden="true" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6829" />
                  </span>
                  <div className="min-w-0 pt-0.5" data-id="element-6830">
                    <div className="flex items-center gap-1.5 mb-1" data-id="element-6831">
                      <span className="font-bold text-[15px] text-[#333333]" data-id="element-6832">
                        Giga
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-200 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-6833">
                        App
                      </span>
                      <span className="text-xs text-neutral-400" data-id="element-6834">9:16 AM</span>
                    </div>
                    <p className="text-[15px] text-[#333333] leading-snug mb-2.5" data-id="element-6835">
                      Hi 👋, great to meet you. I'm Giga, your new AI coworker.
                      Here are two ways to work with me:
                    </p>
                    <p className="text-[15px] text-[#333333] leading-snug mb-2" data-id="element-6836">
                      💬 <span className="font-bold" data-id="element-6837">DM me here</span> — just
                      message me like a coworker. Research, analysis, reports,
                      automation.
                    </p>
                    <p className="text-[15px] text-[#333333] leading-snug" data-id="element-6838">
                      <span className="text-[#1164A3] font-medium" data-id="element-6839">@Giga</span>{' '}
                      <span className="font-bold" data-id="element-6840">in any channel</span> —
                      mention me in context and I'll jump in with the full
                      thread as background.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>;
  }
  if (step === 9) {
    const navItems = [{
      label: 'Threads',
      icon: MessageSquare
    }, {
      label: 'Huddles',
      icon: Headphones
    }, {
      label: 'Recap',
      icon: ArrowRight
    }, {
      label: 'Directories',
      icon: Users
    }];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="h-screen w-full bg-white font-body flex flex-col overflow-hidden" data-id="element-6841">
        {/* macOS chrome & Slack header */}
        <div className="flex items-center gap-4 px-4 py-2 bg-[#3F0E40] border-b border-white/10 shrink-0" data-id="element-6842">
          <div className="flex items-center gap-2 w-16" data-id="element-6843">
            <span className="w-3 h-3 rounded-full bg-[#ff5f57]" data-id="element-6844" />
            <span className="w-3 h-3 rounded-full bg-[#febc2e]" data-id="element-6845" />
            <span className="w-3 h-3 rounded-full bg-[#28c840]" data-id="element-6846" />
          </div>
          <div className="flex-1 flex justify-center" data-id="element-6847">
            <div className="flex items-center gap-2 rounded-md bg-white/20 hover:bg-white/30 transition-colors px-4 py-1.5 text-white/80 text-sm w-full max-w-2xl cursor-text" data-id="element-6848">
              <Search size={14} data-id="element-6849" />
              Search {WORKSPACE_NAME}
            </div>
          </div>
          <div className="w-16 flex justify-end" data-id="element-6850">
            <div className="w-7 h-7 rounded bg-white/20" data-id="element-6851" />
          </div>
        </div>

        <div className="flex flex-1 min-h-0" data-id="element-6852">
          {/* Sidebar */}
          <div className="w-64 shrink-0 bg-[#3F0E40] text-white/80 py-4 flex flex-col overflow-y-auto" data-id="element-6853">
            <div className="px-4 pb-4 mb-2 border-b border-white/10 flex items-center justify-between font-bold text-white text-lg" data-id="element-6854">
              <span className="flex items-center gap-1.5" data-id="element-6855">
                {WORKSPACE_NAME}
                <ChevronDown size={16} data-id="element-6856" />
              </span>
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center" data-id="element-6857">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6858">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" data-id="element-6859"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" data-id="element-6860"></path>
                </svg>
              </div>
            </div>
            <div className="px-3 space-y-0.5 text-[15px]" data-id="element-6861">
              {navItems.map(n => <div key={n.label} className="flex items-center gap-3 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer" data-id="element-6862">
                  <n.icon size={18} className="opacity-70" data-id="element-6863" />
                  {n.label}
                </div>)}
              <div className="h-px bg-white/10 my-4 mx-3" data-id="element-6864" />

              <div className="flex items-center justify-between px-3 py-1.5 group cursor-pointer" data-id="element-6865">
                <span className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity" data-id="element-6866">
                  <ChevronDown size={14} data-id="element-6867" />
                  Channels
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer ml-4" data-id="element-6868">
                <Hash size={16} className="opacity-70" data-id="element-6869" />
                general
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer ml-4" data-id="element-6870">
                <Hash size={16} className="opacity-70" data-id="element-6871" />
                engineering
              </div>

              <div className="h-px bg-white/10 my-4 mx-3" data-id="element-6872" />

              <div className="flex items-center justify-between px-3 py-1.5 group cursor-pointer" data-id="element-6873">
                <span className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity" data-id="element-6874">
                  <ChevronDown size={14} data-id="element-6875" />
                  Direct messages
                </span>
              </div>

              {/* Active Giga DM */}
              <div className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-md bg-[#1164A3] text-white mt-1 cursor-pointer" data-id="element-6876">
                <span className="flex items-center gap-3" data-id="element-6877">
                  <span className="relative w-5 h-5 rounded bg-[#F97316] flex items-center justify-center" data-id="element-6878">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="" aria-hidden="true" className="w-3.5 h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6879" />
                    <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#2BAC76] border-2 border-[#1164A3]" data-id="element-6880" />
                  </span>
                  Giga
                </span>
              </div>

              {[{
              name: 'Priya Patel',
              initial: 'P',
              color: 'bg-[#4A9E8E]',
              online: true
            }, {
              name: 'Marcus Lee',
              initial: 'M',
              color: 'bg-[#9B6DD6]',
              online: true
            }, {
              name: 'Jordan Kim',
              initial: 'J',
              color: 'bg-[#E0727F]',
              online: false
            }, {
              name: 'Elena Rodriguez',
              initial: 'E',
              color: 'bg-[#D9A441]',
              online: false
            }, {
              name: 'Sarah (you)',
              initial: 'S',
              color: 'bg-[#4F84D6]',
              online: true
            }].map(person => <div key={person.name} className="flex items-center gap-3 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer ml-1" data-id="element-6881">
                  <span className={`relative w-5 h-5 rounded ${person.color} flex items-center justify-center text-xs font-bold text-white shrink-0`} data-id="element-6882">
                    {person.initial}
                    {person.online ? <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#2BAC76] border-2 border-[#3F0E40]" data-id="element-6883" /> : <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#3F0E40] border-2 border-white/50" data-id="element-6884" />}
                  </span>
                  {person.name}
                </div>)}
            </div>
          </div>

          {/* Main Chat Area */}
          <div className="flex-1 min-w-0 bg-white flex flex-col" data-id="element-6885">
            {/* Header */}
            <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between shrink-0" data-id="element-6886">
              <div className="flex items-center gap-3" data-id="element-6887">
                <span className="w-8 h-8 rounded bg-[#F97316] flex items-center justify-center" data-id="element-6888">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6889" />
                </span>
                <div className="flex flex-col" data-id="element-6890">
                  <div className="flex items-center gap-2" data-id="element-6891">
                    <span className="font-bold text-[#333333] text-[15px]" data-id="element-6892">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-6893">
                      App
                    </span>
                    <span className="w-2 h-2 rounded-full bg-[#2BAC76]" data-id="element-6894" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-neutral-500" data-id="element-6895">
                <span className="font-semibold text-sm pb-1 border-b-2 border-[#333333] text-[#333333] cursor-pointer" data-id="element-6896">
                  Messages
                </span>
                <span className="font-semibold text-sm pb-1 border-b-2 border-transparent hover:text-[#333333] cursor-pointer" data-id="element-6897">
                  About
                </span>
              </div>
            </div>

            {/* Messages Scroll Area */}
            <div className="flex-1 overflow-y-auto p-5 flex flex-col" data-id="element-6898">
              {/* Intro Block */}
              <div className="mb-8 mt-4" data-id="element-6899">
                <div className="flex items-center gap-4 mb-4" data-id="element-6900">
                  <div className="w-16 h-16 rounded-2xl bg-[#F97316] flex items-center justify-center shadow-sm" data-id="element-6901">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="" aria-hidden="true" className="w-10 h-10 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6902" />
                  </div>
                  <div data-id="element-6903">
                    <div className="flex items-center gap-2 mb-1" data-id="element-6904">
                      <span className="font-bold text-2xl text-[#333333]" data-id="element-6905">
                        Giga
                      </span>
                      <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-6906">
                        App
                      </span>
                      <span className="w-2.5 h-2.5 rounded-full bg-[#2BAC76]" data-id="element-6907" />
                    </div>
                  </div>
                </div>
                <p className="text-[15px] text-[#333333] mb-2" data-id="element-6908">
                  This is the very beginning of your direct message history with{' '}
                  <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded" data-id="element-6909">
                    @Giga
                  </span>
                </p>
                <div className="flex items-center gap-1.5 text-[#1164A3] text-[15px] hover:underline cursor-pointer w-fit" data-id="element-6910">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6911">
                    <circle cx="12" cy="12" r="10" data-id="element-6912"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" data-id="element-6913"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17" data-id="element-6914"></line>
                  </svg>
                  How does Giga work?
                </div>
              </div>

              {/* Date Divider */}
              <div className="relative flex items-center justify-center mb-6" data-id="element-6915">
                <div className="absolute inset-0 flex items-center" data-id="element-6916">
                  <div className="w-full border-t border-neutral-200" data-id="element-6917"></div>
                </div>
                <div className="relative bg-white px-4" data-id="element-6918">
                  <span className="text-[13px] font-bold text-[#333333] border border-neutral-200 rounded-full px-4 py-1 shadow-sm" data-id="element-6919">
                    Today <ChevronDown size={12} className="inline ml-1" data-id="element-6920" />
                  </span>
                </div>
              </div>

              {/* Giga Onboarding Message */}
              <div className="flex items-start gap-3 hover:bg-neutral-50 -mx-5 px-5 py-2 transition-colors" data-id="element-6921">
                <div className="w-10 h-10 rounded-xl bg-[#F97316] flex items-center justify-center shrink-0 mt-0.5" data-id="element-6922">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="" aria-hidden="true" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6923" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-6924">
                  <div className="flex items-center gap-2 mb-1" data-id="element-6925">
                    <span className="font-bold text-[15px] text-[#333333]" data-id="element-6926">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-6927">
                      App
                    </span>
                    <span className="text-xs text-neutral-500 hover:underline cursor-pointer" data-id="element-6928">
                      3:27 PM
                    </span>
                  </div>

                  <div className="text-[15px] text-[#333333] leading-relaxed space-y-3" data-id="element-6929">
                    <p data-id="element-6930">
                      Hi 👋, great to meet you. I am Giga, your new AI coworker.
                      Here are three ways to work with me:
                    </p>

                    <div className="space-y-1.5" data-id="element-6931">
                      <p data-id="element-6932">
                        💬 <span className="font-bold" data-id="element-6933">DM me here</span> — just
                        message me like a coworker. Research, analysis, reports,
                        automation — anything.
                      </p>
                      <p data-id="element-6934">
                        📣{' '}
                        <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded font-medium cursor-pointer" data-id="element-6935">
                          @Giga
                        </span>{' '}
                        <span className="font-bold" data-id="element-6936">in any channel</span> —
                        mention me in context and I'll jump in with the full
                        thread as background.
                      </p>
                      <p data-id="element-6937">
                        🔌{' '}
                        <span className="font-bold" data-id="element-6938">
                          I connect to 3000+ tools
                        </span>{' '}
                        — no tools are connected yet. Need GitHub, Stripe,
                        HubSpot, Google Ads, or anything else? Just tell me and
                        I'll figure out access.
                      </p>
                      <p data-id="element-6939">
                        🔍{' '}
                        <span className="font-bold" data-id="element-6940">
                          I'm now reading through the workspace history
                        </span>{' '}
                        — I'll propose ideas for how I can help over the next
                        hours.
                      </p>
                    </div>

                    <p data-id="element-6941">
                      Try one of these now — just copy, paste, and hit send:
                    </p>

                    <div className="space-y-4" data-id="element-6942">
                      <div data-id="element-6943">
                        <p className="italic text-neutral-600 mb-1" data-id="element-6944">
                          1. In a channel:
                        </p>
                        <div className="border-l-4 border-neutral-200 pl-3 py-0.5" data-id="element-6945">
                          <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded font-medium" data-id="element-6946">
                            @Giga
                          </span>{' '}
                          summarize what's been discussed in this channel this
                          week
                        </div>
                      </div>

                      <div data-id="element-6947">
                        <p className="italic text-neutral-600 mb-1" data-id="element-6948">
                          2. Or right here:
                        </p>
                        <div className="border-l-4 border-neutral-200 pl-3 py-0.5" data-id="element-6949">
                          Research the top 5 trends in our industry and put
                          together a brief report
                        </div>
                      </div>
                    </div>

                    <p data-id="element-6950">
                      Or just tell me what you need — I'll take it from there 🚀
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Message Composer */}
            <div className="px-5 pb-6 pt-2 shrink-0" data-id="element-6951">
              <div className="border border-neutral-400 rounded-xl overflow-hidden focus-within:border-neutral-500 focus-within:ring-1 focus-within:ring-neutral-500 transition-all shadow-sm" data-id="element-6952">
                {/* Toolbar */}
                <div className="bg-neutral-50 border-b border-neutral-200 px-2 py-1.5 flex items-center gap-1" data-id="element-6953">
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-6954">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6955">
                      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" data-id="element-6956"></path>
                      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" data-id="element-6957"></path>
                    </svg>
                  </button>
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-6958">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6959">
                      <line x1="19" y1="4" x2="10" y2="4" data-id="element-6960"></line>
                      <line x1="14" y1="20" x2="5" y2="20" data-id="element-6961"></line>
                      <line x1="15" y1="4" x2="9" y2="20" data-id="element-6962"></line>
                    </svg>
                  </button>
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-6963">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6964">
                      <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" data-id="element-6965"></path>
                      <line x1="4" y1="21" x2="20" y2="21" data-id="element-6966"></line>
                    </svg>
                  </button>
                  <div className="w-px h-4 bg-neutral-300 mx-1" data-id="element-6967" />
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-6968">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6969">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" data-id="element-6970"></path>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" data-id="element-6971"></path>
                    </svg>
                  </button>
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-6972">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6973">
                      <line x1="8" y1="6" x2="21" y2="6" data-id="element-6974"></line>
                      <line x1="8" y1="12" x2="21" y2="12" data-id="element-6975"></line>
                      <line x1="8" y1="18" x2="21" y2="18" data-id="element-6976"></line>
                      <line x1="3" y1="6" x2="3.01" y2="6" data-id="element-6977"></line>
                      <line x1="3" y1="12" x2="3.01" y2="12" data-id="element-6978"></line>
                      <line x1="3" y1="18" x2="3.01" y2="18" data-id="element-6979"></line>
                    </svg>
                  </button>
                </div>

                {/* Input Area */}
                <div className="p-3 min-h-[80px] bg-white" data-id="element-6980">
                  <p className="text-neutral-400 text-[15px]" data-id="element-6981">Message Giga</p>
                </div>

                {/* Bottom Actions */}
                <div className="bg-white px-2 pb-2 flex items-center justify-between" data-id="element-6982">
                  <div className="flex items-center gap-1" data-id="element-6983">
                    <button className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-full" data-id="element-6984">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6985">
                        <line x1="12" y1="5" x2="12" y2="19" data-id="element-6986"></line>
                        <line x1="5" y1="12" x2="19" y2="12" data-id="element-6987"></line>
                      </svg>
                    </button>
                    <button className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-full" data-id="element-6988">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6989">
                        <circle cx="12" cy="12" r="10" data-id="element-6990"></circle>
                        <path d="M8 14s1.5 2 4 2 4-2 4-2" data-id="element-6991"></path>
                        <line x1="9" y1="9" x2="9.01" y2="9" data-id="element-6992"></line>
                        <line x1="15" y1="9" x2="15.01" y2="9" data-id="element-6993"></line>
                      </svg>
                    </button>
                    <button className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-full" data-id="element-6994">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6995">
                        <circle cx="12" cy="12" r="4" data-id="element-6996"></circle>
                        <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94" data-id="element-6997"></path>
                      </svg>
                    </button>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-6998">
                    <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded" data-id="element-6999">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7000">
                        <line x1="22" y1="2" x2="11" y2="13" data-id="element-7001"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2" data-id="element-7002"></polygon>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 10: Welcome back (signed out / sign in to another workspace)
  if (step === 10) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="relative min-h-screen bg-[#FAF8F4] font-body flex flex-col items-center px-6 overflow-hidden" data-id="element-7003">
        {/* Brand cream surface with a soft warm bloom so it stays consistent
                                    with the rest of the auth flow (the "Try Giga free" screen). The
                                    orange primary CTA reads boldly against the cream. */}
        <div aria-hidden="true" className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[760px] h-[760px] rounded-full bg-[#EA580C]/[0.07] blur-[160px]" data-id="element-7004" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full bg-[#EA580C]/[0.05] blur-[150px]" data-id="element-7005" />

        {/* Return home */}
        <Link to="/" className="absolute z-20 top-6 left-6 flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-[#2B211A] transition-colors" data-id="element-7006">
          <ArrowLeft size={16} data-id="element-7007" />
          Back to home
        </Link>

        <div className="relative z-10 w-full max-w-md flex-1 flex flex-col items-center justify-center text-center" data-id="element-7008">
          {/* Logo */}
          <div className="w-16 h-16 rounded-2xl bg-[#F97316] flex items-center justify-center shadow-lg shadow-[#F97316]/20 mb-8" data-id="element-7009">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="Giga" className="w-9 h-9 object-contain [filter:brightness(0)_invert(1)]" data-id="element-7010" />
          </div>

          <h1 className="text-4xl md:text-5xl font-title font-bold text-[#2B211A] tracking-tight mb-4" data-id="element-7011">
            {signedOut ? 'Sign in to Giga' : 'Welcome back'}
          </h1>

          <p className="text-sm text-neutral-500 mb-10" data-id="element-7012">
            {signedOut ? "You've been signed out of all workspaces." : 'Pick up right where you left off.'}
          </p>

          <div className="w-full space-y-3" data-id="element-7013">
            <button onClick={() => navigate('/dashboard')} className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl bg-[#EA580C] hover:bg-[#d24e09] shadow-lg shadow-[#EA580C]/20 transition-all" data-id="element-7014">
              <SlackIcon size={20} data-id="element-7015" />
              <span className="font-semibold text-white" data-id="element-7016">
                Continue with Slack
              </span>
            </button>
            {teamsWaitlistDone ? <div className="w-full flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-xl bg-[#EA580C]/[0.08] border border-[#EA580C]/20 text-[#2B211A]" data-id="element-7017">
                <Check size={18} className="text-[#EA580C]" strokeWidth={2.5} data-id="element-7018" />
                <span className="text-sm font-medium" data-id="element-7019">
                  You're on the Microsoft Teams waitlist — we'll email you when
                  it's ready.
                </span>
              </div> : teamsWaitlistOpen ? <form onSubmit={submitTeamsWaitlist} className="w-full flex flex-col sm:flex-row gap-2" data-id="element-7020">
                <label htmlFor="teams-waitlist-email" className="sr-only" data-id="element-7021">
                  Work email for the Microsoft Teams waitlist
                </label>
                <input id="teams-waitlist-email" type="email" required autoFocus value={teamsWaitlistEmail} onChange={e => setTeamsWaitlistEmail(e.target.value)} placeholder="you@company.com" className="flex-1 py-3.5 px-4 rounded-xl bg-white border border-neutral-200 text-[#2B211A] placeholder:text-neutral-400 shadow-sm focus:outline-none focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/30 transition-all" data-id="element-7022" />
                <button type="submit" className="shrink-0 flex items-center justify-center gap-2 py-3.5 px-5 rounded-xl bg-[#EA580C] hover:bg-[#d24e09] text-white font-semibold shadow-lg shadow-[#EA580C]/20 transition-all" data-id="element-7023">
                  Notify me
                  <ArrowRight size={18} data-id="element-7024" />
                </button>
              </form> : <button onClick={() => setTeamsWaitlistOpen(true)} className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl bg-white border border-neutral-200 text-[#2B211A] shadow-sm hover:bg-neutral-50 hover:border-neutral-300 transition-colors" data-id="element-7025">
                <TeamsIcon size={20} data-id="element-7026" />
                <span className="font-medium" data-id="element-7027">
                  Join Microsoft Teams waitlist
                </span>
              </button>}
          </div>

          {/* Your Workspaces Section — hidden once the user signs out of all
                                                workspaces, leaving a clean sign-in with just the providers above. */}
          {!signedOut && <div className="w-full mt-10" data-id="element-7028">
              <div className="flex items-center gap-4 mb-6" data-id="element-7029">
                <div className="h-px bg-neutral-200 flex-1" data-id="element-7030" />
                <span className="text-[11px] font-bold tracking-wider text-neutral-400 uppercase" data-id="element-7031">
                  Your Workspaces
                </span>
                <div className="h-px bg-neutral-200 flex-1" data-id="element-7032" />
              </div>

              <button onClick={() => navigate('/dashboard')} className="w-full flex items-center justify-between p-4 rounded-xl border border-neutral-200 hover:border-[#EA580C] hover:bg-white transition-all group text-left bg-white shadow-sm" data-id="element-7033">
                <div className="flex items-center gap-4" data-id="element-7034">
                  <div className="w-10 h-10 rounded-lg bg-[#2B211A] border border-black/10 text-white flex items-center justify-center font-bold text-sm" data-id="element-7035">
                    FT
                  </div>
                  <div className="flex flex-col" data-id="element-7036">
                    <span className="font-semibold text-[#2B211A]" data-id="element-7037">
                      Faith Test
                    </span>
                    <span className="text-sm text-neutral-500" data-id="element-7038">
                      alqahtani.faith03@gmail.com
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-7039">
                  <Check size={20} className="text-[#EA580C]" strokeWidth={2.5} data-id="element-7040" />
                  <ArrowRight size={20} className="text-neutral-400 group-hover:text-[#EA580C] transition-colors" data-id="element-7041" />
                </div>
              </button>

              <button onClick={() => setSignedOut(true)} className="mt-6 text-sm text-neutral-500 hover:text-[#2B211A] transition-colors" data-id="element-7042">
                Sign out of all workspaces
              </button>
            </div>}

          <p className="mt-10 text-sm text-neutral-500" data-id="element-7043">
            No account?{' '}
            <button onClick={() => {
            // Send the user to the free-trial start ("Try Giga free")
            // screen — step 1 is the default signup entry.
            setSignedOut(false);
            setStep(1);
            navigate('/signup');
          }} className="font-bold text-[#2B211A] underline hover:text-[#EA580C] transition-colors" data-id="element-7044">
              Sign up
            </button>
          </p>
        </div>

        <p className="relative z-10 max-w-md text-center text-xs text-neutral-400 pb-8" data-id="element-7045">
          By logging in, you agree to the Giga{' '}
          <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-7046">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-7047">
            Terms of Service
          </a>
          .
        </p>
      </motion.div>;
  }
  // Step 11: Trial Paywall
  if (step === 11) {
    const plans = [{
      id: 'plus',
      name: 'Plus',
      desc: 'The ultimate AI work assistant.',
      price: '$49.99/mo'
    }, {
      id: 'pro',
      name: 'Pro',
      desc: 'More usage for power users.',
      price: '$99.99/mo',
      popular: true
    }];
    const selectedPrice = plans.find(p => p.id === plan)?.price.split('/')[0] || '$49.99';
    const futureDate = new Date(Date.now() + 7 * 864e5).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-[#FAF8F4] font-body flex flex-col items-center py-12 px-6" data-id="element-7048">
        {/* Header */}
        <div className="text-center mb-8 flex flex-col items-center" data-id="element-7049">
          <span className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 rounded-full bg-[#FFEDD5] text-[#EA580C] text-xs font-bold uppercase tracking-wider" data-id="element-7050">
            <Check size={13} strokeWidth={3} data-id="element-7051" />
            One last step
          </span>
          <h1 className="text-4xl font-title font-bold text-[#333333] mb-3" data-id="element-7052">
            Start your 7-day free trial
          </h1>
          <p className="text-lg text-neutral-600 mb-6" data-id="element-7053">
            First 7 days free, then{' '}
            <span className="text-[#EA580C] font-bold" data-id="element-7054">{selectedPrice}</span> /
            month
          </p>

          {/* Money-back guarantee — front and center */}
          <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15,
          type: 'spring',
          stiffness: 260,
          damping: 24
        }} className="inline-flex items-center gap-3 pl-2.5 pr-5 py-2.5 rounded-full bg-[#2B211A] text-white shadow-lg shadow-[#2B211A]/30" data-id="element-7055">
            <span className="w-8 h-8 rounded-full bg-[#F97316] text-[#333333] flex items-center justify-center shrink-0" data-id="element-7056">
              <ShieldCheck size={17} strokeWidth={2.4} data-id="element-7057" />
            </span>
            <span className="text-sm font-semibold tracking-tight" data-id="element-7058">
              30-day money-back guarantee
              <span className="hidden sm:inline text-white/60 font-normal" data-id="element-7059">
                {' '}
                · full refund, no questions asked
              </span>
            </span>
          </motion.div>
        </div>

        <div className="w-full max-w-4xl grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12" data-id="element-7060">
          {/* Left Column: Plans & Timeline */}
          <div className="flex flex-col gap-8" data-id="element-7061">
            {/* Plans */}
            <div className="flex flex-col gap-3" data-id="element-7062">
              {plans.map(p => <button key={p.id} onClick={() => setPlan(p.id)} className={`w-full text-left p-5 rounded-2xl border-2 transition-all flex items-center justify-between ${plan === p.id ? 'border-[#EA580C] bg-[#FFEDD5]/40 shadow-sm' : 'border-neutral-200 bg-white hover:border-neutral-300'}`} data-id="element-7063">
                  <div data-id="element-7064">
                    <div className="flex items-center gap-3 mb-1" data-id="element-7065">
                      <span className="font-bold text-[#333333] text-lg" data-id="element-7066">
                        {p.name}
                      </span>
                      {p.popular && <span className="bg-[#EA580C] text-white text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full" data-id="element-7067">
                          Popular
                        </span>}
                    </div>
                    <div className="text-sm text-neutral-500" data-id="element-7068">{p.desc}</div>
                  </div>
                  <div className="font-bold text-[#333333]" data-id="element-7069">{p.price}</div>
                </button>)}
              <p className="text-xs text-neutral-400 px-1 pt-1" data-id="element-7070">
                Need higher usage? More tiers are on the way — you can upgrade
                anytime from Settings.
              </p>
            </div>

            {/* Timeline */}
            <div className="bg-white p-6 rounded-2xl border border-neutral-200 shadow-sm" data-id="element-7071">
              <div className="relative pl-4" data-id="element-7072">
                {/* Vertical Line */}
                <div className="absolute left-[27px] top-4 bottom-4 w-px bg-neutral-200" data-id="element-7073" />

                <div className="flex gap-4 mb-6 relative" data-id="element-7074">
                  <div className="w-7 h-7 rounded-full bg-[#2B211A] text-white flex items-center justify-center shrink-0 z-10" data-id="element-7075">
                    <Check size={14} strokeWidth={3} data-id="element-7076" />
                  </div>
                  <div className="flex-1 pt-1" data-id="element-7077">
                    <div className="font-bold text-[#333333] mb-1" data-id="element-7078">Today</div>
                    <p className="text-sm text-neutral-600 leading-relaxed" data-id="element-7079">
                      Get full access to Giga's features. No charge today.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 relative z-10" data-id="element-7080">
                  <div className="w-7 h-7 rounded-full bg-white border-2 border-neutral-200 flex items-center justify-center shrink-0 z-10" data-id="element-7081">
                    <div className="w-2 h-2 rounded-full bg-neutral-300" data-id="element-7082" />
                  </div>
                  <div className="flex-1 pt-1" data-id="element-7083">
                    <div className="font-bold text-[#333333] mb-1" data-id="element-7084">Day 5</div>
                    <p className="text-sm text-neutral-600 leading-relaxed" data-id="element-7085">
                      We'll email you a reminder that your trial is ending soon.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 relative z-10" data-id="element-7086">
                  <div className="w-7 h-7 rounded-full bg-white border-2 border-neutral-200 flex items-center justify-center shrink-0 z-10" data-id="element-7087">
                    <div className="w-2 h-2 rounded-full bg-neutral-300" data-id="element-7088" />
                  </div>
                  <div className="flex-1 pt-1" data-id="element-7089">
                    <div className="font-bold text-[#333333] mb-1" data-id="element-7090">Day 7</div>
                    <p className="text-sm text-neutral-600 leading-relaxed" data-id="element-7091">
                      Your trial ends. You'll be charged {selectedPrice}/month
                      unless you cancel.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Credit Card Form */}
          <div data-id="element-7092">
            <div className="bg-white p-8 rounded-3xl shadow-xl border border-neutral-200" data-id="element-7093">
              <h3 className="font-bold text-[#333333] text-xl mb-6" data-id="element-7094">
                Payment details
              </h3>

              <div className="space-y-4" data-id="element-7095">
                <div data-id="element-7096">
                  <label className="block text-sm font-bold text-[#333333] mb-2" data-id="element-7097">
                    Card information
                  </label>
                  <div className="relative" data-id="element-7098">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-7099">
                      <CreditCard size={20} data-id="element-7100" />
                    </div>
                    <input type="text" inputMode="numeric" placeholder="0000 0000 0000 0000" className="w-full pl-12 pr-4 py-3 rounded-xl border border-neutral-200 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-all" data-id="element-7101" />
                  </div>
                </div>

                <div data-id="element-7102">
                  <label className="block text-sm font-bold text-[#333333] mb-2" data-id="element-7103">
                    Name on card
                  </label>
                  <input type="text" placeholder="Jane Doe" className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-all" data-id="element-7104" />
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-7105">
                  <div data-id="element-7106">
                    <label className="block text-sm font-bold text-[#333333] mb-2" data-id="element-7107">
                      Expiry
                    </label>
                    <input type="text" placeholder="MM / YY" className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-all" data-id="element-7108" />
                  </div>
                  <div data-id="element-7109">
                    <label className="block text-sm font-bold text-[#333333] mb-2" data-id="element-7110">
                      CVC
                    </label>
                    <input type="text" inputMode="numeric" placeholder="123" className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-all" data-id="element-7111" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-7112">
                  <div data-id="element-7113">
                    <label className="block text-sm font-bold text-[#333333] mb-2" data-id="element-7114">
                      Country
                    </label>
                    <div className="relative" data-id="element-7115">
                      <select className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-all bg-white appearance-none" data-id="element-7116">
                        <option data-id="element-7117">United States</option>
                        <option data-id="element-7118">Canada</option>
                        <option data-id="element-7119">United Kingdom</option>
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" data-id="element-7120">
                        <ChevronDown size={16} data-id="element-7121" />
                      </div>
                    </div>
                  </div>
                  <div data-id="element-7122">
                    <label className="block text-sm font-bold text-[#333333] mb-2" data-id="element-7123">
                      ZIP / Postal
                    </label>
                    <input type="text" placeholder="10001" className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-all" data-id="element-7124" />
                  </div>
                </div>

                <div className="pt-2" data-id="element-7125">
                  <label className="block text-sm font-bold text-[#333333] mb-2" data-id="element-7126">
                    Promo code (optional)
                  </label>
                  <input type="text" placeholder="SAVE20" className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-all" data-id="element-7127" />
                </div>

                <div className="w-full" data-id="element-7128">
                  <label className="block text-sm font-bold text-[#333333] mb-2" data-id="element-7129">
                    Company name
                  </label>
                  <input type="text" placeholder="Acme Corp" className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-all" data-id="element-7130" />
                </div>
              </div>

              <button onClick={() => setStep(8)} className="w-full mt-8 flex items-center justify-center gap-2 py-4 rounded-xl bg-[#2B211A] hover:bg-black text-white font-bold text-lg transition-colors shadow-lg shadow-black/20" data-id="element-7131">
                Start free trial — $0 due today
              </button>

              <div className="flex items-center justify-center gap-2 mt-4 rounded-xl bg-[#F2F5EC] border border-[#DDE6CF] px-4 py-3" data-id="element-7132">
                <ShieldCheck size={18} className="text-[#2B211A] shrink-0" data-id="element-7133" />
                <p className="text-center text-sm font-semibold text-[#2B211A]" data-id="element-7134">
                  30-day money-back guarantee
                </p>
              </div>

              <p className="text-center text-xs text-neutral-500 mt-3" data-id="element-7135">
                7 days free, then {selectedPrice}/month. Cancel anytime — if
                Giga isn't for you, we'll refund every cent, no questions asked.
              </p>
            </div>

            <div className="flex items-center justify-center gap-2 mt-6 text-neutral-400" data-id="element-7136">
              <ShieldCheck size={16} data-id="element-7137" />
              <span className="text-xs font-medium" data-id="element-7138">Secured by Stripe</span>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 1: Landing
  const LANDING_TESTIMONIALS = [{
    quote: "Nothing short of revolutionary. The best attempt I've ever had as a CEO to turn every employee into an AI-first operator.",
    name: 'Tony Ennis',
    title: 'CEO, Brightwave',
    image: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/image_14.png"
  }, {
    quote: "Giga has completely transformed how our engineering team accesses context. It's like having a senior engineer who knows everything.",
    name: 'Sarah Chen',
    title: 'VP Engineering, Northwind',
    image: 'https://i.pravatar.cc/150?u=sarah'
  }, {
    quote: 'The seamless integration with Slack means my team actually uses it. No new tabs, just instant answers where we already work.',
    name: 'Marcus Lee',
    title: 'Product Lead, Acme Corp',
    image: 'https://i.pravatar.cc/150?u=marcus'
  }];
  return <div className="min-h-screen flex font-body bg-white" data-id="element-7139">
      {/* Left Pane - Product Scenery */}
      <div className="hidden lg:flex w-1/2 bg-[#2B211A] border-r border-neutral-800 relative overflow-hidden flex-col p-12" data-id="element-7140">
        {/* Shared dark-green grid + subtle glow */}
        <DarkGreenTexture glow="top-left" data-id="element-7141" />

        <Link to="/" aria-label="Giga home" className="relative z-10 flex items-center gap-2 mb-20 md:mb-24" data-id="element-7142">
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain" data-id="element-7143" />
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/giga4x.png" alt="Giga" className="h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-7144" />
        </Link>

        {/* Product story — auto-cycling feature showcase (Integrations,
           Smart Memory, Autonomous work) */}
        <div className="relative z-10 flex flex-col items-center w-full" data-id="element-7145">
          <SignupShowcase compactCounter counterPosition="bottom" headlinePosition="bottom" data-id="element-7146" />
          {false && <motion.div initial="hidden" animate="show" variants={{
          hidden: {},
          show: {
            transition: {
              staggerChildren: 0.12,
              delayChildren: 0.1
            }
          }
        }} className="w-full max-w-sm flex flex-col items-center gap-6" data-id="element-7147">
              {/* Speed chip */}
              <motion.div variants={{
            hidden: {
              opacity: 0,
              y: -8
            },
            show: {
              opacity: 1,
              y: 0
            }
          }} className="inline-flex items-center gap-2 rounded-full bg-[#F97316] px-4 py-1.5 text-[#2B211A] text-xs font-bold shadow-lg" data-id="element-7148">
                <motion.span animate={{
              scale: [1, 1.3, 1],
              opacity: [1, 0.6, 1]
            }} transition={{
              duration: 1.4,
              repeat: Infinity
            }} className="w-2 h-2 rounded-full bg-[#2B211A]" data-id="element-7149" />
                Live in under 60 seconds
              </motion.div>

              {/* Setup checklist — ticks off fast */}
              <motion.div variants={{
            hidden: {
              opacity: 0,
              y: 12
            },
            show: {
              opacity: 1,
              y: 0
            }
          }} className="w-full bg-white border border-neutral-200 rounded-2xl p-5 shadow-xl shadow-black/5 space-y-3" data-id="element-7150">
                {[{
              label: 'Connected to Slack',
              activeLabel: 'Connecting to Slack…',
              icon: 'slack'
            }, {
              label: 'Synced your tools',
              activeLabel: 'Syncing your tools…',
              tools: true
            }, {
              label: 'Giga is live',
              activeLabel: 'Booting Giga…',
              giga: true
            }].map((item, i) => {
              const isDone = setupStep > i;
              const isActive = setupStep === i;
              return <motion.div key={item.label} variants={{
                hidden: {
                  opacity: 0,
                  x: -10
                },
                show: {
                  opacity: 1,
                  x: 0
                }
              }} className={`relative flex items-center justify-between gap-3 rounded-xl px-2 py-1 transition-colors ${item.giga && isDone ? 'bg-[#F97316]/10' : ''}`} data-id="element-7151">
                      {/* Lime glow payoff on the final 'Giga is live' row */}
                      {item.giga && isDone && <motion.span aria-hidden="true" initial={{
                  opacity: 0
                }} animate={{
                  opacity: [0, 1, 0.5]
                }} transition={{
                  duration: 0.8
                }} className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-[#F97316]/50" data-id="element-7152" />}
                      <div className="relative flex items-center gap-3" data-id="element-7153">
                        <div className="w-6 h-6 shrink-0 flex items-center justify-center" data-id="element-7154">
                          <AnimatePresence mode="wait" data-id="element-7155">
                            {isDone ? <motion.div key="done" initial={{
                        scale: 0
                      }} animate={{
                        scale: 1
                      }} transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 18
                      }} className="w-6 h-6 rounded-full bg-[#F97316] flex items-center justify-center" data-id="element-7156">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2B211A" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7157">
                                  <path d="M20 6L9 17l-5-5" data-id="element-7158" />
                                </svg>
                              </motion.div> : isActive ? <motion.div key="active" className="w-5 h-5 rounded-full border-2 border-neutral-200 border-t-[#EA580C] animate-spin" data-id="element-7159" /> : <motion.div key="pending" className="w-5 h-5 rounded-full border-2 border-neutral-200" data-id="element-7160" />}
                          </AnimatePresence>
                        </div>
                        <span className={`font-medium text-sm transition-colors ${isDone ? 'text-neutral-700' : isActive ? 'text-neutral-500' : 'text-neutral-300'}`} data-id="element-7161">
                          {isActive ? item.activeLabel : item.label}
                        </span>
                      </div>

                      {/* Right-side badges fade in once the row completes */}
                      <AnimatePresence data-id="element-7162">
                        {isDone && item.icon === 'slack' && <motion.div initial={{
                    opacity: 0,
                    scale: 0.8
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} className="w-7 h-7 rounded-lg bg-white flex items-center justify-center shadow-sm border border-neutral-100" data-id="element-7163">
                            <SlackIcon size={16} data-id="element-7164" />
                          </motion.div>}

                        {isDone && item.tools && <div className="flex -space-x-1.5" data-id="element-7165">
                            {['notion', 'linear', 'google-drive', 'figma'].map((slug, j) => <motion.div key={slug} initial={{
                      opacity: 0,
                      scale: 0.8
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} transition={{
                      delay: j * 0.06
                    }} className="w-7 h-7 rounded-full bg-white border-2 border-white shadow-sm flex items-center justify-center overflow-hidden" data-id="element-7166">
                                  <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`} alt={slug} className="w-4 h-4 object-contain" data-id="element-7167" />
                                </motion.div>)}
                          </div>}
                      </AnimatePresence>
                    </motion.div>;
            })}
              </motion.div>
            </motion.div>}
        </div>
      </div>

      {/* Right Pane - Signup Form */}
      <div className="w-full lg:w-1/2 max-lg:min-h-screen flex flex-col items-center justify-start pt-0 lg:p-12 relative bg-[#FAF8F4] overflow-hidden" data-id="element-7168">
        {/* Mobile-only brand texture — a square-dot halftone field that fades in
         at the top and bottom edges and clears through the middle so the form
         stays clean. Hidden on desktop, where the left pane carries the brand. */}
        <div aria-hidden="true" className="lg:hidden dot-matrix z-0" style={{
        WebkitMaskImage: 'linear-gradient(to bottom, black 0%, transparent 28%, transparent 72%, black 100%)',
        maskImage: 'linear-gradient(to bottom, black 0%, transparent 28%, transparent 72%, black 100%)'
      }} data-id="element-7169" />

        {/* Mobile-only brand lockup — pinned to the very top-left of the screen.
         On desktop (lg+) the left pane already shows the Giga logo. */}
        <Link to="/" aria-label="Giga home" className="flex lg:hidden items-center gap-2 absolute top-6 left-6 z-20" data-id="element-7170">
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="" aria-hidden="true" className="w-8 h-8 object-contain" data-id="element-7171" />
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/giga4x.png" alt="Giga" className="h-5 object-contain" data-id="element-7172" />
        </Link>

        {/* Invisible spacer to match the left pane's logo height + bottom margin,
           ensuring the two headlines align perfectly on desktop. */}
        <div className="hidden lg:block h-7 mb-20 md:mb-24" aria-hidden="true" data-id="element-7173" />

        <div className="relative z-10 w-full max-w-[400px] flex flex-col flex-1 lg:flex-none justify-center lg:justify-start px-6 md:px-8 lg:px-0 pt-20 lg:pt-0 pb-6 lg:pb-0" data-id="element-7174">
          {!isLogin && <span className="block text-xs font-semibold tracking-[5px] uppercase text-[#EA580C] mb-3 md:mb-4 text-center lg:text-left" data-id="element-7175">
              Try Giga Free
            </span>}
          <h1 className="font-title font-medium text-[34px] md:text-5xl tracking-tight text-[#2B211A] leading-[1.1] mb-4 md:mb-5 text-center lg:text-left" data-id="element-7176">
            {isLogin ? 'Welcome back. Giga never slacked off.' : 'Your entire work stack, one AI brain.'}
          </h1>

          {/* One short subline — keeps the panel scannable so the CTAs sit
           high. Risk-reversal copy lives below the buttons instead. */}
          <p className="hidden md:block text-base text-neutral-600 leading-relaxed mb-8 max-w-md" data-id="element-7177">
            {isLogin ? 'Sign in to hand off the busywork and get back to the good stuff.' : 'Connect all your tools, ask questions, take action — all from one conversation.'}
          </p>

          {/* Social Proof — extra top gap on mobile, where the subline above is
           hidden, so it doesn't crowd the headline. */}
          <div className="flex items-center justify-center lg:justify-start gap-3 md:gap-4 mt-4 md:mt-0 mb-5 md:mb-8" data-id="element-7178">
            <div className="flex -space-x-2 md:-space-x-2.5" data-id="element-7179">
              {['https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=80&h=80&fit=crop&crop=faces&grayscale', 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80&h=80&fit=crop&crop=faces&grayscale', 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=80&h=80&fit=crop&crop=faces&grayscale', 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&h=80&fit=crop&crop=faces&grayscale'].map((src, i) => <img key={i} src={src} alt="" aria-hidden="true" className="w-6 h-6 md:w-8 md:h-8 rounded-full object-cover border-2 border-white shadow-sm relative" style={{
              zIndex: 4 - i
            }} data-id="element-7180" />)}
            </div>
            <div className="flex flex-col gap-0.5" data-id="element-7181">
              <div className="flex items-center gap-0.5" aria-hidden="true" data-id="element-7182">
                {[0, 1, 2, 3, 4].map(i => <Star key={i} className="w-2.5 h-2.5 md:w-3 md:h-3 fill-[#F59E0B] text-[#F59E0B]" data-id="element-7183" />)}
              </div>
              <span className="text-[9px] md:text-[11px] font-bold uppercase tracking-wider text-neutral-500" data-id="element-7184">
                4.9/5 from 1,200+ teams
              </span>
            </div>
          </div>

          {/* Mobile-only notification illustration — sits below the trial copy
           and rating so the value prop leads, with a quiet "tasks completed"
           caption beneath the live feed. */}
          <div className="lg:hidden w-full flex justify-center mb-6" data-id="element-7185">
            <SignupShowcase variant="light" counterPosition="bottom" compactCounter data-id="element-7186" />
          </div>

          <div className="flex flex-col gap-3" data-id="element-7187">
            <motion.button whileHover={{
            scale: 1.01,
            y: -1
          }} whileTap={{
            scale: 0.99
          }} transition={{
            duration: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} onClick={() => {
            if (isLogin) {
              navigate('/dashboard');
            } else {
              setStep(2);
            }
          }} className="w-full flex items-center justify-center gap-3 px-4 py-3 md:py-3.5 rounded-xl bg-[#2B211A] text-white font-bold transition-shadow hover:shadow-lg hover:shadow-black/5" data-id="element-7188">
              <SlackIcon size={20} data-id="element-7189" />
              Continue with Slack
            </motion.button>
            <motion.button whileHover={{
            scale: 1.01,
            y: -1
          }} whileTap={{
            scale: 0.99
          }} transition={{
            duration: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} onClick={() => {
            if (isLogin) {
              navigate('/dashboard');
            } else {
              setStep(2);
            }
          }} className="w-full flex items-center justify-center gap-3 px-4 py-3 md:py-3.5 rounded-xl border border-[#EAE4D9] bg-white text-[#2B211A] font-bold transition-all hover:border-[#D4D0C8] hover:bg-neutral-50 hover:shadow-sm" data-id="element-7190">
              <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/microsoft-teams.svg" alt="Teams" className="w-5 h-5" data-id="element-7191" />
              Continue with Teams
            </motion.button>
          </div>

          {/* Risk reversal — trial-only, right at the moment of decision.
           Hidden on /login, where there's no trial to reverse. */}
          {!isLogin && <div className="flex items-center justify-center gap-x-2 md:gap-x-3 mt-4 md:mt-5 text-[11px] md:text-[13px] font-medium text-neutral-500 whitespace-nowrap" data-id="element-7192">
              <span className="flex items-center gap-1 md:gap-1.5" data-id="element-7193">
                <Check className="w-3 h-3 md:w-[13px] md:h-[13px] text-[#EA580C]" strokeWidth={3} data-id="element-7194" />
                Cancel anytime
              </span>
              <span className="w-1 h-1 rounded-full bg-neutral-300 shrink-0" data-id="element-7195" />
              <span className="flex items-center gap-1 md:gap-1.5" data-id="element-7196">
                <Check className="w-3 h-3 md:w-[13px] md:h-[13px] text-[#EA580C]" strokeWidth={3} data-id="element-7197" />
                30-day money-back guarantee
              </span>
            </div>}

          <p className="text-center text-[11px] text-neutral-400 mt-10 pt-0 mb-0 lg:mt-8 lg:pt-0 leading-relaxed max-w-[320px] mx-auto" data-id="element-7198">
            By continuing, you agree to Giga's{' '}
            <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-7199">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-7200">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>;
}